import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { load as parseYaml } from 'js-yaml';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { SimilarDatasetsComponent } from '../../../shared/components/similar-datasets/similar-datasets.component';
import { AssetCardData } from '../../../shared/components/asset-card/asset-card.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { DateRangePickerComponent, DateRange } from '../../../shared/components/primeng/date-range-picker/date-range-picker.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { CatalogueApiService, AclApdApiService, AuthApiService, SearchCriteria, RsApiService, UserInteractionApiService, FileApiService, DiscussionApiService } from '@angular-workspace/services';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { DiscussionItem, DiscussionAttachment } from '../../discussions/discussion-home/discussion-home.component';
import { CreateDiscussionOffcanvasComponent, DiscussionFormData } from '../../../shared/components/create-discussion-offcanvas/create-discussion-offcanvas.component';
import { AssetDiscussionListComponent } from '../../../shared/components/asset-discussion-list/asset-discussion-list.component';
import { SqlPlaygroundComponent } from '../sql-visualisation/sql-playground.component';
import { DatasetVisualisationComponent } from '../visualisation/dataset-visualisation.component';
import { ModelPopupComponent } from '../../../shared/components/popups/model-popup/model-popup.component';
import { GatewayDataComponent } from '../../../shared/components/popups/gateway-data/gateway-data.component';
import { OgcStacDataComponent } from '../../../shared/components/popups/ogc-stac-data/ogc-stac-data.component';
import { OgcFeatureDataComponent } from '../../../shared/components/popups/ogc-feature-data/ogc-feature-data.component';
import { environment } from '../../../../environments/environment';

interface ResourceServer {
  readonly name: string;
  readonly accessTypes?: readonly string[];
  readonly queryTypes?: readonly string[];
}

interface TryOutSpec {
  openapi?: string;
  info?: {
    title?: string;
    version?: string;
    description?: string;
  };
  servers?: Array<{ url: string; description?: string }>;
  paths?: Record<string, Record<string, any>>;
}

interface TryOutParameter {
  name: string;
  in: 'path' | 'query' | 'header';
  required: boolean;
  description: string;
  type: string;
  example: string;
}

interface TryOutOperation {
  id: string;
  method: string;
  path: string;
  summary: string;
  description: string;
  parameters: TryOutParameter[];
  requestContentType: string | null;
  requestBodyExample: string;
}

interface TryOutRequestState {
  params: Record<string, string>;
  bodyText: string;
}

interface TryOutExecutionResult {
  loading: boolean;
  status?: number;
  statusText?: string;
  body?: string;
  error?: string;
  curl?: string;
}

interface VersionFileEntry {
  name: string;
  size?: string;
  isSelected?: boolean;
  lastModified?: string;
}

interface VersionFileTreeNode {
  type: 'folder' | 'file';
  name: string;
  path: string;
  apiPrefix?: string;
  children?: VersionFileTreeNode[];
  fileRef?: VersionFileEntry;
  childCount?: number;
  lastModified?: string;
  childrenLoaded?: boolean;
  isLoading?: boolean;
}

interface VersionFileFlatNode {
  node: VersionFileTreeNode;
  depth: number;
}

@Component({
  selector: 'app-dataset-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    SimilarDatasetsComponent,
    ModalComponent,
    LoginModalComponent,
    CreateDiscussionOffcanvasComponent,
    AssetDiscussionListComponent,
    SqlPlaygroundComponent,
    DatasetVisualisationComponent,
    DateRangePickerComponent,
    PdfViewerModule,
    ModelPopupComponent,
    GatewayDataComponent,
    OgcStacDataComponent,
    OgcFeatureDataComponent,
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class DatasetDetailComponent implements OnInit, OnDestroy {
  private catalogueApiService = inject(CatalogueApiService);
  private aclApdApiService = inject(AclApdApiService);
  private authApiService = inject(AuthApiService);
  private rsApiService = inject(RsApiService);
  private fileApiService = inject(FileApiService);
  private helperService = inject(HelperService);
  private formatService = inject(FormatService);
  private toasterService = inject(ToasterService);
  private keycloak = inject(KeycloakService);
  private keycloakSubscription?: Subscription;
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private http = inject(HttpClient);
  private discussionApiService = inject(DiscussionApiService);
  private sanitizer = inject(DomSanitizer);
  
  datasetId: string | null = null;
  readonly fileTryOutUrl = '/documents/file.yaml';
  readonly ngsiLdTryOutUrl = '/documents/ngsi-ld.yaml';
  private loadedTryOutYamlUrl: string | null = null;
  /** Keep Try Out available on dataset detail page. */
  showTryOutButton = false;
  showTryOutModal = false;
  tryOutYamlLoading = false;
  tryOutYamlError = '';
  tryOutSpec: TryOutSpec | null = null;
  tryOutOperations: TryOutOperation[] = [];
  selectedTryOutServerUrl = '';
  expandedTryOutOperationId: string | null = null;
  tryOutRequestState: Record<string, TryOutRequestState> = {};
  tryOutExecutionResults: Record<string, TryOutExecutionResult> = {};
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Datasets', route: '/datasets' },
      { label: 'Dataset Details' }
    ],
    title: 'Dataset Details',
    description: 'Loading dataset information...'
  };
  similarDatasets: AssetCardData[] = [];
  datasetDescription: string = '';
  datasetTags: string[] = [];
  showTagsModal: boolean = false;
  readonly MAX_VISIBLE_TAGS = 8; // Approximate number of tags that fit in 2 rows
  additionalInformation: string = '';
  
  // Activity Overview metrics
  activityMetrics = {
    likes: 0,
    views: 0,
    bookmarks: 0
  };
  
  // Tab state
  activeTab: 'dataset' | 'details' | 'visualisation' | 'sql-playground' | 'discussion' = 'dataset';
  isLoggedIn: boolean = false;
  pendingTab: 'dataset' | 'details' | 'visualisation' | 'sql-playground' | 'discussion' | null = null;
  showSampleDataModal: boolean = false;
  /** Sample data from item API dataSample; null when API does not return dataSample. */
  sampleData: any = null;
  showDataDescriptorsModal: boolean = false;
  selectedDataElement: string = '';
  showViewCodeModal: boolean = false;
  viewCodeActiveTab: string = '';
  viewCodeTabs: Array<{ name: string; resourceServer: any }> = [];
  viewCodeByTab: { [key: string]: string } = {};
  showQueryDataModal: boolean = false;
  showRequestAccessModal: boolean = false;
  showLoginModal: boolean = false;
  showGetDataModal: boolean = false;
  
  // Get Data Modal state
  getDataLoading: boolean = false;
  getDataResponse: any = null;
  getDataCurrentPage: number = 1;
  getDataPageSize: number = 8;
  getDataTotalPages: number = 1;
  getDataTotalEntries: number = 0;
  getDataEntries: any[] = [];
  /** 'entity' = NGSI-LD table; 'file' = file from file server shown in PDF viewer */
  getDataMode: 'entity' | 'file' = 'entity';
  /** For FILE mode: URL to show in PDF viewer (presigned or blob). */
  getDataPdfUrl: string | null = null;
  /** For FILE mode non-PDF: download URL and filename. */
  getDataFileDownloadUrl: string | null = null;
  getDataFileName: string = '';

  /** Get Data - Gateway/OGC popup state (server-specific modals) */
  isFeatureDataOpen = false;
  featureDataMode: {
    type: 'GATEWAY' | 'OGC_STAC' | 'OGC_FEATURE' | 'DEFAULT';
    dataModel?: 'model1' | 'model2';
    accessTypes?: readonly string[];
  } = {
    type: 'GATEWAY',
    dataModel: 'model1',
    accessTypes: [],
  };
  
  queryDataForm: {
    requestAs: 'Individual' | 'Organisation';
    organisationName: string;
    purposeDescription: string;
    agreeToTerms: boolean;
  } = {
    requestAs: 'Organisation',
    organisationName: '',
    purposeDescription: '',
    agreeToTerms: false
  };
  isOrganisationNameDisabled: boolean = false;
  /** True when user has joined/created an organisation (from getUserInfo). When true, modal shows only Organisation Name (disabled) + form; when false, shows Individual/Organisation choice. */
  userHasOrganisation: boolean = false;
  readonly PURPOSE_MAX_LENGTH = 200;

  /** Query Data modal: date range filter (single picker, range mode like asset filters) */
  queryDataDateRange: DateRange = { startDate: null, endDate: null };
  /** True while temporal entities API is in progress (shows loader in modal). */
  queryDataLoading: boolean = false;
  /** JSON response from temporal entities API – shown below dates, used for Download JSON. */
  queryDataResponse: any = null;
  /** Used to cancel the temporal entities API when user closes the Query Data modal. */
  queryDataAbortController: AbortController | null = null;

  /** View File modal: open file (PDF/DOCX) in a modal with optional tabs */
  showViewFileModal: boolean = false;
  viewFileActiveTab: 'pdf' | 'docx' = 'pdf';
  viewFilePdfUrl: string | null = null;
  viewFileDocxHtml: string = '';
  viewFileDocxDownloadUrl: string | null = null;
  viewFileDocxFileName: string = '';
  viewFileLoading: boolean = false;

  // Discussion data
  datasetDiscussions: DiscussionItem[] = [];
  datasetDiscussionsLoading: boolean = false;
  datasetDiscussionsError: string | null = null;
  
  // Create Discussion Offcanvas
  showCreateDiscussionOffcanvas: boolean = false;
  
  viewCode: string = `import requests
import json

# === CONFIGURATION ===
CONTROL_PLANE_URL = "https://staging.controlplane.forestdx.iudx.io/iudx/v2/auth/token"
ENTITIES_URL = "https://staging.rs.forestdx.iudx.io/ngsi-ld/v2/entities/3c6e7ea7-4884-43b9-8179-71b3db86284b"
CLIENT_ID = "Please use your client details"
CLIENT_SECRET = "Please use your client details"
TIMEOUT = 10

def get_access_token():
    """Fetch access token from Control Plane API"""
    headers = {
        "clientId": CLIENT_ID,
        "clientSecret": CLIENT_SECRET
    }
    print("🔐 Requesting access token...")
    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)
    resp.raise_for_status()
    data = resp.json()
    token = data.get("result", {}).get("access_token")
    if not token:
        raise ValueError("❌ access_token not found in response JSON")
    print("✅ Access token retrieved successfully")
    return token

def fetch_entities_paginated(access_token):
    """Fetch entities from Resource Server across all pages."""
    headers = {
        "Authorization": f"Bearer {access_token}"
    }
    print("\\n📡 Fetching entities data with pagination...")
    all_data = []
    page = 1
    # Call first page to determine totalPages
    first_url = f"{ENTITIES_URL}?page={page}"
    resp = requests.get(first_url, headers=headers, timeout=TIMEOUT)
    resp.raise_for_status()
    data = resp.json()
    all_data.append(data)
    pagination_info = data.get("paginationInfo", {})
    total_pages = pagination_info.get("totalPages", 1)
    print(f"ℹ️ Total pages available: {total_pages}")
    # Loop through remaining pages if any
    for page in range(2, total_pages + 1):
        url = f"{ENTITIES_URL}?page={page}"
        print(f"➡️ Fetching page {page}/{total_pages} ...")
        resp = requests.get(url, headers=headers, timeout=TIMEOUT)
        resp.raise_for_status()
        page_data = resp.json()
        all_data.append(page_data)
    print(f"\\n✅ Fetched {len(all_data)} pages successfully.")
    return all_data

def main():
    try:
        token = get_access_token()
        pages_data = fetch_entities_paginated(token)
        # Optional: combine results if entities are in a list
        combined_entities = []
        for page_json in pages_data:
            if isinstance(page_json, dict) and "results" in page_json:
                combined_entities.extend(page_json["results"])
            elif isinstance(page_json, list):
                combined_entities.extend(page_json)
        print(f"\\n📊 Total entities collected: {len(combined_entities)}")
        # Save to file
        with open("response.json", "w", encoding="utf-8") as f:
            json.dump(pages_data, f, indent=2)
        print("💾 Saved results to response.json")
    except Exception as e:
        print(f"❌ Error: {e}")

if __name__ == "__main__":
    main()`;
  /** Data descriptors from item API dataDescriptor (name, label, dataSchema, description, type). */
  dataDescriptors: Array<{
    name: string;
    label?: string;
    dataSchema: string;
    description: string;
    type?: string | string[];
  }> = [];
  datasetDetails: {
    uploadedBy?: string;
    organisationType?: string;
    industry?: string;
    organizationName?: string;
    yearRange?: string;
    datasetPermission?: string;
    lastUpdated?: string;
    fileFormat?: string;
    license?: string;
    uploadFrequency?: string;
    dataReadiness?: number;
    verifiedBy?: string;
    resourceType?: string;
    accessPolicy?: string;
    [key: string]: any;
  } = {};
  
  datasetCardDetails: {
    badge?: string;
    createdAt?: string;
    updatedAt?: string;
    fileFormat?: string;
    uploadFrequency?: string;
    verifiedBy?: string;
    dataReadiness?: string;
  } = {};
  
  // PDF Viewer modal state
  showDataReadinessPdfModal: boolean = false;
  dataReadinessPdfUrl: string | null = null;
  
  accessStatus: {
    type?: string;
    [key: string]: any;
  } = {};
  currentUserSub: string | null = null;
  private hasLoadedCurrentUserInfo = false;

  // Store full dataset data to access resourceServer
  fullDatasetData: any = null;
  
  // Version Files data for Details tab
  versionFiles: VersionFileEntry[] = [];
  versionFileTree: VersionFileTreeNode[] = [];
  visibleVersionFileNodes: VersionFileFlatNode[] = [];
  expandedVersionFolderPaths = new Set<string>();
  versionFileSearchTerm = '';
  readonly maxVisibleVersionFileNodes = 1000;
  visibleVersionFileNodeLimitReached = false;
  versionFilesTotalSize: string = '';
  versionFilesLastUpdated: string = '';
  /** Preview API responses keyed by file name (populated after files API 200 OK). */
  versionFilePreviews: Record<string, { data: any; status: number }> = {};
  /** Parsed table for Details tab Data Bank Viewer (from selected file's preview). */
  detailsPreviewHeaders: string[] = [];
  detailsPreviewEntries: any[] = [];

  // Details tab data loading state
  detailsTabDataLoading: boolean = false;
  /** True while loading preview for the selected file (on file click). */
  detailsPreviewLoading: boolean = false;
  /** File metadata from getFileMetadata API (displayed below Version Files). */
  fileMetadata: any = null;
  /** True while loading file metadata. */
  fileMetadataLoading: boolean = false;
  
  // Bookmark and Like state
  isBookmarked: boolean = false;
  isLiked: boolean = false;
  
  private route = inject(ActivatedRoute);
  private userInteractionApiService = inject(UserInteractionApiService);
  private googleAnalytics = inject(GoogleAnalyticsService);

  get visibleTags(): string[] {
    return this.datasetTags.slice(0, this.MAX_VISIBLE_TAGS);
  }

  get remainingTagsCount(): number {
    return Math.max(0, this.datasetTags.length - this.MAX_VISIBLE_TAGS);
  }

  get allTags(): string[] {
    return this.datasetTags;
  }

  get hasDatasetDetails(): boolean {
    return this.datasetDetails && Object.keys(this.datasetDetails).length > 0;
  }

  /** Comma-separated file keys (from files API) to show to the left of "Version Files" heading. */
  get versionFileKeys(): string {
    if (!this.versionFiles?.length) return '';
    return this.versionFiles.map(f => f.name).join(', ');
  }

  /** Version files that are PDF (by extension). */
  get versionFilesPdf(): Array<{ name: string; size?: string; isSelected?: boolean }> {
    if (!this.versionFiles?.length) return [];
    return this.versionFiles.filter(f => (f.name || '').toLowerCase().endsWith('.pdf'));
  }

  /** Version files that are DOCX/DOC (by extension). */
  get versionFilesDocx(): Array<{ name: string; size?: string; isSelected?: boolean }> {
    if (!this.versionFiles?.length) return [];
    return this.versionFiles.filter(f => {
      const n = (f.name || '').toLowerCase();
      return n.endsWith('.docx') || n.endsWith('.doc');
    });
  }

  get hasViewFilePdf(): boolean {
    return this.versionFilesPdf.length > 0;
  }

  get hasViewFileDocx(): boolean {
    return this.versionFilesDocx.length > 0;
  }

  get hasViewFileMultipleTypes(): boolean {
    return this.hasViewFilePdf && this.hasViewFileDocx;
  }

  get hasVisibleVersionFileNodes(): boolean {
    return this.visibleVersionFileNodes.length > 0;
  }

  /** True when datasetType matches (e.g. 'TEMPORAL') - from root or any resource server. */
  private hasDatasetType(type: string): boolean {
    if (!this.fullDatasetData) return false;
    const upper = type.toUpperCase();
    const rootType = (this.fullDatasetData.datasetType ?? this.fullDatasetData.dataBankType ?? '').toString().toUpperCase();
    if (rootType === upper) return true;
    const rs = this.fullDatasetData.resourceServer;
    if (!rs) return false;
    const resourceServers = Array.isArray(rs) ? rs : [rs];
    return resourceServers.some((r: any) => (r?.datasetType ?? '').toString().toUpperCase() === upper);
  }

  /** True when type (e.g. 'ATTR' or 'TEMPORAL') is in resourceServer[].queryTypes or in fullDatasetData.iudxResourceAPIs. */
  private hasQueryType(type: string): boolean {
    if (!this.fullDatasetData) return false;
    const upper = type.toUpperCase();
    const apis = this.fullDatasetData.iudxResourceAPIs;
    if (Array.isArray(apis) && apis.some((t: string) => String(t).toUpperCase() === upper)) return true;
    const rs = this.fullDatasetData.resourceServer;
    if (!rs) return false;
    const resourceServers = Array.isArray(rs) ? rs : [rs];
    return resourceServers.some((r: any) => {
      const queryTypes = r?.queryTypes;
      return Array.isArray(queryTypes) && queryTypes.some((t: string) => String(t).toUpperCase() === upper);
    });
  }

  get shouldShowGetDataButton(): boolean {
    if (!this.fullDatasetData) return false;
    // Show for NGSI-LD (ATTR) or for FILE-only datasets (get data from file server)
    if (this.hasQueryType('ATTR')) return true;
    return this.hasFileResource();
  }

  /** True when FILE is in resourceServer (name, datasetType, or resourceType) or in iudxResourceAPIs. */
  private hasFileResource(): boolean {
    if (!this.fullDatasetData) return false;
    const apis = this.fullDatasetData.iudxResourceAPIs;
    if (Array.isArray(apis) && apis.some((t: string) => String(t).toUpperCase() === 'FILE')) return true;
    const rs = this.fullDatasetData.resourceServer;
    if (!rs) return false;
    const resourceServers = Array.isArray(rs) ? rs : [rs];
    return resourceServers.some((r: any) => {
      const name = (r?.name ?? '').toString().toUpperCase();
      const datasetType = (r?.datasetType ?? '').toString().toUpperCase();
      const resourceType = (r?.resourceType ?? '').toString().toUpperCase();
      return name === 'FILE' || datasetType === 'FILE' || resourceType === 'FILE';
    });
  }

  /** Show Sample Data and Data descriptors only when datasetType=TEMPORAL and queryTypes includes ATTR. */
  get shouldShowSampleDataAndDescriptors(): boolean {
    return !this.shouldShowViewFile && this.hasDatasetType('TEMPORAL') && this.hasQueryType('ATTR');
  }

  /** True when the item API returned a non-empty dataSample (show Sample Data button and modal). */
  get hasDataSample(): boolean {
    const d = this.sampleData;
    if (d == null) return false;
    if (typeof d === 'string') return d.trim().length > 0;
    if (Array.isArray(d)) return d.length > 0;
    if (typeof d === 'object') return Object.keys(d).length > 0;
    return true;
  }

  /** True when the item API returned dataDescriptor (show Data Descriptors button and modal). */
  get hasDataDescriptor(): boolean {
    return Array.isArray(this.dataDescriptors) && this.dataDescriptors.length > 0;
  }

  /** Show View File button only when the dataset has only FILE (no ATTR or TEMPORAL). */
  get shouldShowViewFile(): boolean {
    if (!this.hasFileResource()) return false;
    if (this.hasQueryType('ATTR') || this.hasQueryType('TEMPORAL')) return false;
    return true;
  }

  /** Normalized list of resource servers for display (name, url, queryTypes). */
  get resourceServersList(): any[] {
    if (!this.fullDatasetData || !this.fullDatasetData.resourceServer) {
      return [];
    }
    const rs = this.fullDatasetData.resourceServer;
    return Array.isArray(rs) ? rs : [rs];
  }

  /** Resource server section layout: 'cards' | 'table' | 'list' (attribute-style rows). */
  resourceServerDesign: 'cards' | 'table' | 'list' = 'table';

  async ngOnInit(): Promise<void> {
    // Read tab from query parameters FIRST (before login check to preserve it)
    let tabFromUrl: string | null = null;
    try {
      const queryParams = await firstValueFrom(this.route.queryParams);
      tabFromUrl = queryParams['tab'] as string;
      console.log('[Tab Persistence] Tab from URL:', tabFromUrl);
    } catch (error) {
      console.error('[Tab Persistence] Error reading query params:', error);
    }

    // Check login status on initialization - wait a bit for Keycloak to initialize
    try {
      // Give Keycloak a moment to initialize if needed
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Try multiple methods to check login status
      let isLoggedInCheck = false;
      try {
        isLoggedInCheck = await this.keycloak.isLoggedIn();
      } catch (e) {
        console.log('[Tab Persistence] Keycloak.isLoggedIn() failed, trying alternative check');
      }
      
      // Fallback: Check if we can get the token from Keycloak
      if (!isLoggedInCheck) {
        try {
          const token = await this.keycloak.getToken();
          isLoggedInCheck = !!token;
        } catch (e) {
          // If that fails, check localStorage
          const localToken = localStorage.getItem('ai-sandbox-cat-auth-token');
          isLoggedInCheck = !!localToken;
        }
      }
      
      this.isLoggedIn = isLoggedInCheck;
      console.log('[Tab Persistence] Login status:', this.isLoggedIn);
      if (this.isLoggedIn) {
        await this.loadCurrentUserInfo();
      }
    } catch (error) {
      console.error('Error checking login status:', error);
      // Fallback: Check localStorage for token
      const token = localStorage.getItem('ai-sandbox-cat-auth-token');
      this.isLoggedIn = !!token;
      console.log('[Tab Persistence] Using localStorage fallback, isLoggedIn:', this.isLoggedIn);
      if (this.isLoggedIn) {
        await this.loadCurrentUserInfo();
      }
    }

    // Now restore the tab based on URL and login status
    if (tabFromUrl && ['dataset', 'details', 'visualisation', 'sql-playground', 'discussion'].includes(tabFromUrl)) {
      const tab = tabFromUrl as 'dataset' | 'details' | 'visualisation' | 'sql-playground' | 'discussion';
      
      // Check if tab requires login
      const requiresLogin = tab === 'details' || tab === 'visualisation' || tab === 'sql-playground';
      
      if (requiresLogin && !this.isLoggedIn) {
        // If tab requires login but user is not logged in, stay on dataset tab
        console.log('[Tab Persistence] Tab requires login but user not logged in, defaulting to dataset');
        this.activeTab = 'dataset';
        // Only update URL if it's different from what we want
        if (tabFromUrl !== 'dataset') {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: 'dataset' },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      } else {
        // Set the tab and load data if needed
        console.log('[Tab Persistence] Setting active tab to:', tab, 'isLoggedIn:', this.isLoggedIn);
        this.activeTab = tab;
        if (tab === 'discussion') {
          this.loadDatasetDiscussions();
        } else if (tab === 'details' || tab === 'visualisation') {
          this.loadDetailsTabData();
        }
        // Don't update URL - it's already correct, preserve the tab parameter
      }
    } else {
      // No tab in URL, set default and add it
      if (!tabFromUrl) {
        console.log('[Tab Persistence] No tab in URL, setting default to dataset');
        this.activeTab = 'dataset';
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: 'dataset' },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    }

    this.route.paramMap.subscribe(params => {
      this.datasetId = params.get('id');
      console.log('Dataset Detail Component initialized with ID:', this.datasetId);
      if (!this.datasetId) {
        console.error('No dataset ID found in route parameters');
        return;
      }
      // Load dataset details from API using this.datasetId
      // loadSimilarDatasets() is called inside loadDatasetDetails() after tags are loaded
      this.loadDatasetDetails();
    });

    // Subscribe to Keycloak events to call hasAccess when user logs in
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          // User has logged in, update login status and call hasAccess API
          this.isLoggedIn = true;
          this.hasLoadedCurrentUserInfo = false;
          await this.loadCurrentUserInfo(true);
          console.log('User logged in, calling checkUserAccess');
          await this.checkUserAccess();
          await this.checkBookmarkAndLikeStatus();
          
          // If there's a pending tab, switch to it after login
          await this.onLoginSuccess();
        } else if (keycloakEvent.type === KeycloakEventType.OnAuthLogout) {
          // User has logged out, update login status
          this.isLoggedIn = false;
          this.currentUserSub = null;
          this.hasLoadedCurrentUserInfo = false;
          this.isBookmarked = false;
          this.isLiked = false;
          this.pendingTab = null; // Clear any pending tab on logout
        }
      }
    );
  }

  async setActiveTab(tab: 'dataset' | 'details' | 'visualisation' | 'sql-playground' | 'discussion'): Promise<void> {
    // Check if user needs to be logged in for this tab
    const requiresLogin = tab === 'details' || tab === 'visualisation' || tab === 'sql-playground';
    
    if (requiresLogin) {
      // Check if user is logged in
      try {
        const isLoggedIn = await this.keycloak.isLoggedIn();
        this.isLoggedIn = isLoggedIn;
        
        if (!isLoggedIn) {
          // Store the tab the user wanted to access
          this.pendingTab = tab;
          // Show login modal instead of switching tabs
          this.showLoginModal = true;
          return;
        }
      } catch (error) {
        console.error('Error checking login status:', error);
        // On error, store the tab and show login modal to be safe
        this.pendingTab = tab;
        this.showLoginModal = true;
        return;
      }
    }
    
    // User is logged in or tab doesn't require login, proceed with tab switch
    this.activeTab = tab;
    
    // Update URL query parameters to persist tab state
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    
    if (tab === 'discussion') {
      this.loadDatasetDiscussions();
    } else if (tab === 'details' || tab === 'visualisation' || tab === 'sql-playground') {
      this.loadDetailsTabData();
    }
  }
  
  /**
   * Handle successful login - switch to pending tab if one exists
   */
  async onLoginSuccess(): Promise<void> {
    // Update login status
    this.isLoggedIn = true;
    
    // If there's a pending tab, switch to it
    if (this.pendingTab) {
      const tabToSwitch = this.pendingTab;
      this.pendingTab = null; // Clear pending tab
      
      // Switch to the tab the user originally wanted
      this.activeTab = tabToSwitch;
      
      // Update URL query parameters to persist tab state
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabToSwitch },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
      
      if (tabToSwitch === 'discussion') {
        this.loadDatasetDiscussions();
      } else if (tabToSwitch === 'details' || tabToSwitch === 'visualisation' || tabToSwitch === 'sql-playground') {
        this.loadDetailsTabData();
      }
    }
  }
  
  /**
   * Extract files array from files API response. Handles multiple shapes:
   * - { files: [...] }
   * - { data: { files: [...] } }
   * - { success: true, data: { files: [...], directories: [] } }
   */
  private extractFilesFromFilesResponse(data: any): any[] {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.files)) return data.files;
    const inner = data.data;
    if (inner && typeof inner === 'object' && Array.isArray(inner.files)) return inner.files;
    return [];
  }

  private extractDirectoriesFromFilesResponse(data: any): any[] {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.directories)) return data.directories;
    const inner = data.data;
    if (inner && typeof inner === 'object' && Array.isArray(inner.directories)) return inner.directories;
    return [];
  }

  private normalizeDirectoryPrefix(prefix: string): string {
    const trimmed = (prefix || '').trim();
    if (!trimmed) return '';
    return trimmed.replace(/\/+$/, '');
  }

  private getNodeDisplayName(path: string): string {
    const normalized = this.normalizeDirectoryPrefix(path);
    if (!normalized) return '';
    const parts = normalized.split('/').filter(Boolean);
    return parts[parts.length - 1] || normalized;
  }

  private sortVersionFileNodes(nodes: VersionFileTreeNode[]): void {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }

  private createNodesFromListing(apiFiles: any[], apiDirectories: any[]): VersionFileTreeNode[] {
    const fileNodes: VersionFileTreeNode[] = apiFiles
      .filter((file: any) => file.isFile !== false)
      .map((file: any, index: number) => {
        const fileName = file.key || `file_${index + 1}`;
        const entry: VersionFileEntry = {
          name: fileName,
          size: (file.size || 0) > 0 ? this.formatFileSize(file.size || 0) : undefined,
          isSelected: !!this.versionFiles.find((existing) => existing.name === fileName)?.isSelected,
          lastModified: file.lastModified,
        };
        return {
          type: 'file',
          name: this.getNodeDisplayName(fileName),
          path: fileName,
          fileRef: entry,
          lastModified: file.lastModified,
        };
      });

    const directoryNodes: VersionFileTreeNode[] = apiDirectories.map((dir: any) => {
      const normalized = this.normalizeDirectoryPrefix(dir.key || '');
      return {
        type: 'folder',
        name: this.getNodeDisplayName(normalized),
        path: normalized,
        apiPrefix: normalized ? `${normalized}/` : '',
        childCount: dir.childCount ?? 0,
        lastModified: dir.lastModified,
        children: [],
        childrenLoaded: false,
        isLoading: false,
      };
    });

    const nodes = [...directoryNodes, ...fileNodes];
    this.sortVersionFileNodes(nodes);
    return nodes;
  }

  private createNodesFromRecursiveFiles(apiFiles: any[], prefix: string): VersionFileTreeNode[] {
    const rootNodes: VersionFileTreeNode[] = [];
    const folderIndex = new Map<string, VersionFileTreeNode>();
    const normalizedPrefix = prefix || '';

    for (const file of apiFiles.filter((item: any) => item.isFile !== false)) {
      const fullPath = (file.key || '').trim();
      if (!fullPath) continue;

      const relativePath = normalizedPrefix && fullPath.startsWith(normalizedPrefix)
        ? fullPath.slice(normalizedPrefix.length)
        : fullPath;
      const parts = relativePath.split('/').filter(Boolean);
      if (parts.length === 0) continue;

      let currentChildren = rootNodes;
      let currentPath = this.normalizeDirectoryPrefix(normalizedPrefix);

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLeaf = i === parts.length - 1;
        const nextPath = currentPath ? `${currentPath}/${part}` : part;

        if (isLeaf) {
          const entry: VersionFileEntry = {
            name: fullPath,
            size: (file.size || 0) > 0 ? this.formatFileSize(file.size || 0) : undefined,
            isSelected: !!this.versionFiles.find((existing) => existing.name === fullPath)?.isSelected,
            lastModified: file.lastModified,
          };
          currentChildren.push({
            type: 'file',
            name: part,
            path: fullPath,
            fileRef: entry,
            lastModified: file.lastModified,
          });
          continue;
        }

        let folderNode = folderIndex.get(nextPath);
        if (!folderNode) {
          folderNode = {
            type: 'folder',
            name: part,
            path: nextPath,
            apiPrefix: `${nextPath}/`,
            children: [],
            childrenLoaded: true,
            isLoading: false,
          };
          folderIndex.set(nextPath, folderNode);
          currentChildren.push(folderNode);
        }
        currentChildren = folderNode.children ?? [];
        currentPath = nextPath;
      }
    }

    const sortRecursively = (nodes: VersionFileTreeNode[]): void => {
      this.sortVersionFileNodes(nodes);
      nodes.forEach((node) => {
        if (node.type === 'folder' && node.children?.length) {
          sortRecursively(node.children);
        }
      });
    };

    sortRecursively(rootNodes);
    return rootNodes;
  }

  private mergeVersionFiles(entries: VersionFileEntry[]): void {
    const selectedFileName = this.versionFiles.find((file) => file.isSelected)?.name;
    const merged = new Map<string, VersionFileEntry>();

    for (const file of this.versionFiles) {
      merged.set(file.name, file);
    }
    for (const entry of entries) {
      const existing = merged.get(entry.name);
      merged.set(entry.name, {
        ...existing,
        ...entry,
        isSelected: existing?.isSelected || selectedFileName === entry.name,
      });
    }

    this.versionFiles = Array.from(merged.values());
  }

  private async fetchVersionFileListing(
    prefix = '',
    recursive = false
  ): Promise<{ files: any[]; directories: any[] }> {
    if (!this.datasetId) return { files: [], directories: [] };
    const response = await this.fileApiService.getFilesForPreview(this.datasetId, {
      prefix,
      delimiter: recursive ? '' : '/',
      maxKeys: this.maxVisibleVersionFileNodes,
      recursive,
    });
    return {
      files: this.extractFilesFromFilesResponse(response?.data),
      directories: this.extractDirectoriesFromFilesResponse(response?.data),
    };
  }

  private async loadVersionFileChildren(node: VersionFileTreeNode): Promise<void> {
    if (!this.datasetId || node.type !== 'folder' || node.childrenLoaded || node.isLoading) return;

    node.isLoading = true;
    this.recomputeVisibleVersionFileNodes();
    this.cdr.markForCheck();

    try {
      const effectivePrefix = node.apiPrefix || `${node.path}/`;
      const { files } = await this.fetchVersionFileListing(effectivePrefix, true);
      node.children = this.createNodesFromRecursiveFiles(files, effectivePrefix);
      node.childrenLoaded = true;
      const loadedEntries = node.children
        .flatMap((child) => {
          const collect = (treeNode: VersionFileTreeNode): VersionFileEntry[] => {
            if (treeNode.type === 'file' && treeNode.fileRef) return [treeNode.fileRef];
            return (treeNode.children || []).flatMap(collect);
          };
          return collect(child);
        });
      this.mergeVersionFiles(loadedEntries);
    } catch (error) {
      console.error('Error loading directory children:', node.path, error);
      node.children = [];
      node.childrenLoaded = true;
    } finally {
      node.isLoading = false;
      this.recomputeVisibleVersionFileNodes();
      this.cdr.markForCheck();
    }
  }

  private folderHasSearchMatch(node: VersionFileTreeNode, normalizedTerm: string): boolean {
    if (!normalizedTerm) return true;
    const selfMatches =
      node.name.toLowerCase().includes(normalizedTerm) ||
      node.path.toLowerCase().includes(normalizedTerm);
    if (selfMatches) return true;
    if (node.type !== 'folder' || !node.children?.length) return false;
    return node.children.some((child) =>
      child.type === 'folder'
        ? this.folderHasSearchMatch(child, normalizedTerm)
        : child.name.toLowerCase().includes(normalizedTerm) || child.path.toLowerCase().includes(normalizedTerm)
    );
  }

  private recomputeVisibleVersionFileNodes(): void {
    const normalizedTerm = this.versionFileSearchTerm.trim().toLowerCase();
    const searching = normalizedTerm.length > 0;
    const flattened: VersionFileFlatNode[] = [];
    this.visibleVersionFileNodeLimitReached = false;

    const pushNode = (node: VersionFileTreeNode, depth: number): boolean => {
      if (flattened.length >= this.maxVisibleVersionFileNodes) {
        this.visibleVersionFileNodeLimitReached = true;
        return false;
      }
      flattened.push({ node, depth });
      return true;
    };

    const visit = (nodes: VersionFileTreeNode[], depth: number): boolean => {
      for (const node of nodes) {
        if (node.type === 'folder') {
          const includeFolder = !searching || this.folderHasSearchMatch(node, normalizedTerm);
          if (!includeFolder) continue;
          if (!pushNode(node, depth)) return false;
          const shouldTraverseChildren = searching || this.expandedVersionFolderPaths.has(node.path);
          if (shouldTraverseChildren && node.children?.length) {
            if (!visit(node.children, depth + 1)) return false;
          }
        } else {
          const includeFile =
            !searching ||
            node.name.toLowerCase().includes(normalizedTerm) ||
            node.path.toLowerCase().includes(normalizedTerm);
          if (!includeFile) continue;
          if (!pushNode(node, depth)) return false;
        }
      }
      return true;
    };

    visit(this.versionFileTree, 0);
    this.visibleVersionFileNodes = flattened;
  }

  private rebuildVersionFileTree(nodes?: VersionFileTreeNode[]): void {
    if (nodes) {
      this.versionFileTree = nodes;
    }
    this.recomputeVisibleVersionFileNodes();
  }

  onVersionFileSearchChange(): void {
    this.recomputeVisibleVersionFileNodes();
  }

  isVersionFolderExpanded(path: string): boolean {
    return this.expandedVersionFolderPaths.has(path);
  }

  async toggleVersionFolder(path: string): Promise<void> {
    const findNode = (nodes: VersionFileTreeNode[]): VersionFileTreeNode | null => {
      for (const node of nodes) {
        if (node.path === path) return node;
        if (node.type === 'folder' && node.children?.length) {
          const childMatch = findNode(node.children);
          if (childMatch) return childMatch;
        }
      }
      return null;
    };
    const targetNode = findNode(this.versionFileTree);

    if (this.expandedVersionFolderPaths.has(path)) {
      this.expandedVersionFolderPaths.delete(path);
    } else {
      this.expandedVersionFolderPaths.add(path);
      if (targetNode?.type === 'folder') {
        await this.loadVersionFileChildren(targetNode);
      }
    }
    this.recomputeVisibleVersionFileNodes();
  }

  async onVersionFileNodeClick(node: VersionFileTreeNode): Promise<void> {
    if (node.type === 'folder') {
      await this.toggleVersionFolder(node.path);
      return;
    }
    if (!node.fileRef) return;
    this.mergeVersionFiles([node.fileRef]);
    await this.onDetailsFileSelect(node.fileRef);
  }

  async loadDetailsTabData(): Promise<void> {
    // Load version files and preview data for Details tab
    this.detailsTabDataLoading = true;
    
    try {
      // Call getFilesForPreview API if datasetId is available
      if (this.datasetId) {
        try {
          const { files: apiFiles, directories: apiDirectories } = await this.fetchVersionFileListing('');
          
          // Process the API response
          if (apiFiles.length >= 0 || apiDirectories.length >= 0) {
            // Map API files to component structure
            const files: VersionFileEntry[] = [];
            
            if (apiFiles.length > 0 || apiDirectories.length > 0) {
              let totalSizeBytes = 0;
              let latestModifiedDate: Date | null = null;
              
              apiFiles.forEach((file: any, index: number) => {
                // Only process files (not directories)
                if (file.isFile !== false) {
                  const fileName = file.key || `file_${index + 1}`;
                  const fileSizeBytes = file.size || 0;
                  totalSizeBytes += fileSizeBytes;
                  
                  // Track latest modified date
                  if (file.lastModified) {
                    const modifiedDate = new Date(file.lastModified);
                    if (!latestModifiedDate || modifiedDate > latestModifiedDate) {
                      latestModifiedDate = modifiedDate;
                    }
                  }
                  
                  files.push({
                    name: fileName,
                    size: fileSizeBytes > 0 ? this.formatFileSize(fileSizeBytes) : undefined,
                    isSelected: false,
                    lastModified: file.lastModified
                  });
                }
              });

              apiDirectories.forEach((dir: any) => {
                if (dir.lastModified) {
                  const modifiedDate = new Date(dir.lastModified);
                  if (!latestModifiedDate || modifiedDate > latestModifiedDate) {
                    latestModifiedDate = modifiedDate;
                  }
                }
              });
              
              this.versionFiles = files;
              this.rebuildVersionFileTree(this.createNodesFromListing(apiFiles, apiDirectories));
              
              // Set total size
              this.versionFilesTotalSize = totalSizeBytes > 0 
                ? this.formatFileSize(totalSizeBytes) 
                : '';
              
              // Set last updated from the latest file modification date
              if (latestModifiedDate) {
                const dateValue = latestModifiedDate as Date;
                this.versionFilesLastUpdated = this.formatService.formatDate(dateValue.toISOString()) || '';
              } else {
                this.versionFilesLastUpdated = this.datasetDetails.lastUpdated || 
                  this.datasetCardDetails.createdAt || 
                  '';
              }
              
              // Sync preview state (empty when no file selected).
              this.loadPreviewForSelectedFileIfNeeded();
              this.cdr.markForCheck();
            } else {
              // No files found
              this.versionFiles = [];
              this.versionFileTree = [];
              this.visibleVersionFileNodes = [];
              this.visibleVersionFileNodeLimitReached = false;
              this.versionFilesTotalSize = '';
              this.versionFilesLastUpdated = '';
              this.versionFilePreviews = {};
              this.detailsPreviewHeaders = [];
              this.detailsPreviewEntries = [];
              this.fileMetadata = null;
            }
            this.cdr.markForCheck();
          }
        } catch (error) {
          console.error('Error fetching files for preview:', error);
          // On error, set empty files
          this.versionFiles = [];
          this.versionFileTree = [];
          this.visibleVersionFileNodes = [];
          this.visibleVersionFileNodeLimitReached = false;
          this.versionFilesTotalSize = '';
          this.versionFilesLastUpdated = '';
          this.versionFilePreviews = {};
          this.detailsPreviewHeaders = [];
          this.detailsPreviewEntries = [];
          this.fileMetadata = null;
        }
      } else {
        // No datasetId available
        this.versionFiles = [];
        this.versionFileTree = [];
        this.visibleVersionFileNodes = [];
        this.visibleVersionFileNodeLimitReached = false;
        this.versionFilesTotalSize = '';
        this.versionFilesLastUpdated = '';
        this.versionFilePreviews = {};
        this.detailsPreviewHeaders = [];
        this.detailsPreviewEntries = [];
        this.fileMetadata = null;
      }
      
      // Do not auto-call entity API (ngsi-ld/v2/entities) when Details tab loads.
      // Entity data is loaded only when the user clicks "Get Data" and opens the Get Data modal.
    } catch (error) {
      console.error('Error loading details tab data:', error);
      // On error, set empty files
      this.versionFiles = [];
      this.versionFileTree = [];
      this.visibleVersionFileNodes = [];
      this.visibleVersionFileNodeLimitReached = false;
      this.versionFilesTotalSize = '';
      this.versionFilesLastUpdated = '';
      this.versionFilePreviews = {};
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
      this.fileMetadata = null;
    } finally {
      this.detailsTabDataLoading = false;
    }
  }

  /**
   * Extract headers and rows from a preview API response (same shape as Visualisation tab).
   * Handles data.content.preview, data.content, and success/data wrapper.
   */
  private extractPreviewTable(previewRes: { data: any; status: number } | null): { headers: string[]; entries: any[] } {
    const empty = { headers: [] as string[], entries: [] as any[] };
    if (!previewRes?.data || typeof previewRes.data !== 'object') return empty;

    let actualData = previewRes.data;
    if (previewRes.data?.success && previewRes.data?.data !== undefined) {
      actualData = previewRes.data.data;
    }
    if (actualData?.data?.content !== undefined) {
      actualData = { content: actualData.data.content, format: actualData.data.format || actualData.format };
    }

    const content = actualData?.content || (actualData?.headers ? actualData : null);
    if (!content) return empty;

    // API may put headers/data in content.preview (e.g. xlsx)
    const block = content.preview && typeof content.preview === 'object' ? content.preview : content;
    const headers: string[] = Array.isArray(block?.headers) ? block.headers : [];
    const data = block?.data;
    const entries = Array.isArray(data) ? data : [];

    return { headers, entries };
  }

  /** Set detailsPreviewHeaders and detailsPreviewEntries from the currently selected file's preview. */
  syncDetailsPreviewFromSelectedFile(): void {
    const selected = this.versionFiles.find(f => f.isSelected);
    if (!selected) {
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
      return;
    }
    const previewRes = this.versionFilePreviews[selected.name];
    const { headers, entries } = this.extractPreviewTable(previewRes ?? null);
    this.detailsPreviewHeaders = headers;
    this.detailsPreviewEntries = entries;
  }

  /** Load preview for the currently selected file if not already cached. */
  async loadPreviewForSelectedFileIfNeeded(): Promise<void> {
    const selected = this.versionFiles.find(f => f.isSelected);
    if (!selected || !this.datasetId) {
      this.syncDetailsPreviewFromSelectedFile();
      this.cdr.markForCheck();
      return;
    }
    if (this.versionFilePreviews[selected.name]) {
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
      this.cdr.markForCheck();
      return;
    }
    this.detailsPreviewLoading = true;
    this.cdr.markForCheck();
    try {
      const previewRes = await this.fileApiService.generatePreview(this.datasetId, selected.name);
      this.versionFilePreviews[selected.name] = previewRes;
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
    } catch (err) {
      console.warn('Preview failed for', selected.name, err);
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
    } finally {
      this.detailsPreviewLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Load file metadata for the currently selected version file. Displayed below Version Files section. */
  async loadFileMetadataForSelectedFile(): Promise<void> {
    const selected = this.versionFiles.find(f => f.isSelected);
    if (!selected || !this.datasetId) {
      this.fileMetadata = null;
      this.cdr.markForCheck();
      return;
    }
    this.fileMetadataLoading = true;
    this.fileMetadata = null;
    this.cdr.markForCheck();
    try {
      const res = await this.fileApiService.getFileMetadata(this.datasetId, { key: selected.name });
      const raw = res?.data;
      // Handle success/data wrapper (e.g. { success: true, data: { ... } })
      this.fileMetadata = raw?.success && raw?.data != null ? raw.data : raw;
    } catch (err) {
      console.warn('File metadata failed for', selected.name, err);
      this.fileMetadata = null;
    } finally {
      this.fileMetadataLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Called when user clicks a file in the Details tab Version Files list. Loads preview only for that file. */
  async onDetailsFileSelect(file: { name: string; size?: string; isSelected?: boolean }): Promise<void> {
    this.versionFiles = this.versionFiles.map(f => ({
      ...f,
      isSelected: f.name === file.name,
    }));
    this.recomputeVisibleVersionFileNodes();
    this.scrollToTabView();

    const cached = this.versionFilePreviews[file.name];
    if (cached) {
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
      this.cdr.markForCheck();
      return;
    }

    if (!this.datasetId) {
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
      this.cdr.markForCheck();
      return;
    }

    this.detailsPreviewLoading = true;
    this.cdr.markForCheck();

    try {
      const previewRes = await this.fileApiService.generatePreview(this.datasetId, file.name);
      this.versionFilePreviews[file.name] = previewRes;
      this.syncDetailsPreviewFromSelectedFile();
      this.loadFileMetadataForSelectedFile();
    } catch (err) {
      console.warn('Preview failed for', file.name, err);
      this.detailsPreviewHeaders = [];
      this.detailsPreviewEntries = [];
    } finally {
      this.detailsPreviewLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Scroll the page so the tab row (Dataset, Details, Visualisation, etc.) is in view, below the header. */
  private scrollToTabView(): void {
    setTimeout(() => {
      const tabsEl = document.getElementById('dataset-detail-tabs');
      if (!tabsEl) return;
      const headerOffset = 100;
      const rect = tabsEl.getBoundingClientRect();
      const scrollTop = window.scrollY ?? document.documentElement.scrollTop;
      const targetY = scrollTop + rect.top - headerOffset;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }, 0);
  }

  /** File size for selected file: from metadata (bytes → MB) or fallback to aggregate. */
  get selectedFileSizeDisplay(): string {
    if (this.fileMetadata?.size != null) {
      const bytes = typeof this.fileMetadata.size === 'string' ? parseInt(this.fileMetadata.size, 10) : this.fileMetadata.size;
      return !isNaN(bytes) ? this.formatFileSize(bytes) : this.versionFilesTotalSize;
    }
    return this.versionFilesTotalSize;
  }

  /** Last updated for selected file: from metadata (ISO → DD-MMM-YYYY HH:MM) or fallback. */
  get selectedFileLastUpdatedDisplay(): string {
    if (this.fileMetadata?.lastModified) {
      return this.formatService.formatDate(this.fileMetadata.lastModified) || this.versionFilesLastUpdated;
    }
    return this.versionFilesLastUpdated;
  }

  /** Name of the currently selected version file, for viewer title. */
  get selectedVersionFileName(): string {
    if (!this.versionFiles || this.versionFiles.length === 0) {
      return '';
    }
    const selected = this.versionFiles.find(f => f.isSelected);
    return selected?.name || '';
  }

  getDetailsTableValue(entry: any, header: string): string {
    const value = entry != null && typeof entry === 'object' && header in entry ? entry[header] : undefined;
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
  
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
  
  getTableHeaders(): string[] {
    if (!this.getDataEntries || this.getDataEntries.length === 0) {
      return [];
    }
    
    const headerSet = new Set<string>();
    this.getDataEntries.forEach(entry => {
      this.flattenObject(entry, '', headerSet);
    });
    
    // Return first 10 headers for preview
    return Array.from(headerSet).slice(0, 10);
  }
  
  getTableValue(entry: any, header: string): string {
    const value = this.getNestedValue(entry, header);
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  async loadDatasetDiscussions(): Promise<void> {
    if (!this.datasetId) {
      this.datasetDiscussions = [];
      return;
    }
    this.datasetDiscussionsLoading = true;
    this.datasetDiscussionsError = null;
    this.cdr.detectChanges();
    try {
      const response = await this.discussionApiService.getDiscussionChoices({
        filters: {
          type: [],
          status: ['APPROVED'],
          tags: [],
          sub_category_id: this.datasetId
        },
        pinned: true,
        sort_by: 'newest',
        page: 1,
        limit: 10
      });
      const discussions = response?.data?.discussions ?? response?.discussions ?? [];
      this.datasetDiscussions = (Array.isArray(discussions) ? discussions : []).map((item: any) =>
        this.mapDiscussionChoiceToItem(item)
      );
    } catch (error) {
      console.error('Error loading dataset discussions:', error);
      this.datasetDiscussionsError = 'Failed to load discussions. Please try again.';
      this.datasetDiscussions = [];
    } finally {
      this.datasetDiscussionsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapDiscussionChoiceToItem(item: any): DiscussionItem {
    const author = item?.user?.name ?? 'Unknown';
    const subCategory = item?.sub_category ?? this.headerConfig.title ?? '';
    const publishedTime = item?.published_time ?? item?.created_at ?? '';
    return {
      id: item?.id ?? '',
      title: item?.title ?? '',
      description: `${author} · ${subCategory}`,
      author,
      category: item?.category ?? 'DATA_BANKS',
      replies: item?.votes ?? 0,
      views: item?.view_count ?? 0,
      lastActivity: publishedTime ? `Published ${this.formatRelativeTime(publishedTime)}` : '',
      isPinned: item?.is_pinned ?? false,
      isBookmarked: item?.is_bookmarked ?? false,
      isVoted: item?.is_voted ?? false,
      status: item?.status,
      subCategoryId: item?.sub_category_id,
      assetType: 'Dataset',
      assetName: subCategory
    };
  }

  private formatRelativeTime(dateString: string): string {
    const dateObj = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return dateObj.toLocaleDateString();
  }

  navigateToDiscussion(discussionId: string): void {
    this.discussionApiService.getReview(discussionId).then(() => {
      this.router.navigate(['/discussions', discussionId]);
    }).catch(() => {
      this.router.navigate(['/discussions', discussionId]);
    });
  }

  async createNewDiscussion(): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.router.navigate(['/discussions/create-new'], {
      queryParams: { category: 'GETTING_STARTED', tab: 'getting-started' }
    });
  }

  closeCreateDiscussionOffcanvas(): void {
    this.showCreateDiscussionOffcanvas = false;
  }

  onDiscussionPublish(formData: DiscussionFormData): void {
    // TODO: Implement API call to publish discussion
    console.log('Publishing discussion:', formData);
    // Close offcanvas and reload discussions
    this.closeCreateDiscussionOffcanvas();
    this.loadDatasetDiscussions();
  }

  onDiscussionClose(): void {
    this.closeCreateDiscussionOffcanvas();
  }

  get discussionInitialValues(): Partial<DiscussionFormData> {
    return {
      assetCategory: 'Dataset',
      assetId: this.datasetId || '',
      assetName: this.headerConfig.title || ''
    };
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  /**
   * Handle bookmark button click
   */
  async onBookmarkClick(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    if (!this.datasetId) {
      console.error('Cannot bookmark: datasetId is null');
      return;
    }

    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      
      const payload = {
        assetId: this.datasetId,
        assetType: 'DATABANK',
        actionType: actionType
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);
      
      // Update local state
      this.isBookmarked = !this.isBookmarked;
      if (this.isBookmarked) {
        this.toasterService.success("You've bookmarked the Dataset successfully.");
      } else {
        this.toasterService.success('Bookmark removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  /**
   * Handle like button click
   */
  async onLikeClick(): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    if (!this.datasetId) {
      console.error('Cannot like: datasetId is null');
      return;
    }

    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';
      
      const payload = {
        assetId: this.datasetId,
        assetType: 'DATABANK',
        actionType: actionType
      };

      await this.userInteractionApiService.createBookmarkOrLike(payload);
      
      // Update local state
      this.isLiked = !this.isLiked;
      // Optimistically update like count immediately (item API may have eventual consistency)
      this.activityMetrics.likes = Math.max(0, (this.activityMetrics.likes ?? 0) + (this.isLiked ? 1 : -1));
      if (this.isLiked) {
        this.toasterService.success("You've liked the Dataset successfully.");
      } else {
        this.toasterService.success("You've Unliked the Dataset successfully.");
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }

  /** Refresh activity metrics from item API (e.g. after like/bookmark). Use API values as-is; calling getItemById may increment view on server, so prefer optimistic update for like and avoid calling this after like to prevent extra view count. */
  private async refreshActivityMetrics(): Promise<void> {
    if (!this.datasetId) return;
    try {
      const response = await this.catalogueApiService.getItemById(this.datasetId);
      let data = response;
      if (response?.result) data = Array.isArray(response.result) ? response.result[0] : response.result;
      else if (response?.data) data = Array.isArray(response.data) ? response.data[0] : response.data;
      else if (response?.items?.[0]) data = response.items[0];
      else if (Array.isArray(response) && response.length > 0) data = response[0];
      if (!data) return;
      const metrics = data.metrics || data;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.views = metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;
    } catch {
      // Ignore - keep existing metrics
    }
  }

  /**
   * Check if the dataset is bookmarked and liked by the user
   */
  async checkBookmarkAndLikeStatus(): Promise<void> {
    if (!this.isLoggedIn || !this.datasetId) {
      return;
    }

    try {
      // Fetch all user interactions for this assetId on page load
      const response = await this.userInteractionApiService.userInteractions({
        assetId: this.datasetId,
        assetType: 'DATABANK',
      });

      // Extract items from possible response shapes
      const items: any[] =
        (response?.result && Array.isArray(response.result) ? response.result : null) ||
        (response?.results && Array.isArray(response.results) ? response.results : null) ||
        (response?.data && Array.isArray(response.data) ? response.data : null) ||
        [];

      // API may return: result[0].userInteraction.{ bookmarked, liked } (new) or result[0].assetDetails / top-level bookmarked, liked (legacy)
      const first = items[0] ?? {};
      const userInteraction = first?.userInteraction;
      const ad = first?.assetDetails ?? first;

      const bookmarked =
        typeof userInteraction?.bookmarked === 'boolean' ? userInteraction.bookmarked :
        typeof first?.isBookmarked === 'boolean' ? first.isBookmarked :
        typeof first?.bookmarked === 'boolean' ? first.bookmarked :
        typeof ad?.bookmarked === 'boolean' ? ad.bookmarked : null;
      if (bookmarked !== null) {
        this.isBookmarked = bookmarked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isBookmarked = actionTypes.includes('BOOKMARK');
      }

      const liked =
        typeof userInteraction?.liked === 'boolean' ? userInteraction.liked :
        typeof first?.isLiked === 'boolean' ? first.isLiked :
        typeof first?.liked === 'boolean' ? first.liked :
        typeof ad?.liked === 'boolean' ? ad.liked : null;
      if (liked !== null) {
        this.isLiked = liked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isLiked = actionTypes.includes('LIKE');
      }

      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking bookmark/like status:', error);
      // Don't show error to user - silently fail
    }
  }


  async loadDatasetDetails(): Promise<void> {
    if (!this.datasetId) {
      console.error('Cannot load dataset details: datasetId is null or undefined');
      return;
    }
    
    try {
      console.log('Loading dataset details for ID:', this.datasetId);
      
      // Call API: <basepathurl><apiCatPat>/item?id=<id of the dataset>
      const response = await this.catalogueApiService.getItemById(this.datasetId);
      console.log('API Response received:', response);
      
      // Handle response structure - could be direct object or nested in result
      let datasetData = response;
      if (response?.result) {
        datasetData = Array.isArray(response.result) ? response.result[0] : response.result;
      } else if (response?.data) {
        datasetData = Array.isArray(response.data) ? response.data[0] : response.data;
      } else if (response?.items && Array.isArray(response.items) && response.items.length > 0) {
        datasetData = response.items[0];
      } else if (Array.isArray(response) && response.length > 0) {
        datasetData = response[0];
      }
      
      console.log('Extracted datasetData:', datasetData);
      console.log('DatasetData type:', typeof datasetData);
      console.log('DatasetData is object:', datasetData && typeof datasetData === 'object');
      
      if (!datasetData) {
        console.error('No dataset data found in response. Response structure:', response);
        // Update header to show error state
        this.headerConfig.description = 'Failed to load dataset information. Please try again later.';
        this.cdr.detectChanges();
        return;
      }
      
      console.log('Dataset data loaded:', datasetData);
      console.log('Dataset data keys:', Object.keys(datasetData));
      
      // Extract short description (use shortDescription if available, otherwise truncate description)
      let shortDescription = datasetData.shortDescription || datasetData.summary || '';
      if (!shortDescription && datasetData.description) {
        // Truncate description to first 150 characters if no short description available
        shortDescription = datasetData.description.length > 150 
          ? datasetData.description.substring(0, 150).trim() + '...'
          : datasetData.description;
      }
      
      // Extract organization name for subtitle
      const organizationName = datasetData.provider || datasetData.organisation || datasetData.organization || '';
      
      // Update header config with actual dataset data
      const datasetTitle = datasetData.label || datasetData.name || 'Dataset Details';
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Datasets', route: '/datasets' },
          { label: datasetTitle }
        ],
        title: datasetTitle,
        description: shortDescription || 'Loading dataset information...',
        assetId: this.datasetId || datasetData.id || ''
      };
      
      // Store organization name for subtitle display
      if (organizationName && this.datasetDetails) {
        this.datasetDetails.uploadedBy = organizationName;
      }
      
      // Extract description
      this.datasetDescription = datasetData.description || datasetData.shortDescription || datasetData.summary || '';
      
      // Extract tags
      this.datasetTags = [];
      if (datasetData.tags && Array.isArray(datasetData.tags)) {
        this.datasetTags = datasetData.tags;
      } else if (datasetData.tag && Array.isArray(datasetData.tag)) {
        this.datasetTags = datasetData.tag;
      }
      
      console.log('Dataset description:', this.datasetDescription);
      console.log('Dataset tags:', this.datasetTags);
      
      // Extract dataset details
      // Extract organisation type from organizationType field
      const organisationType = datasetData.organizationType || datasetData.organisationType || 'Public';
      
      // Extract industry from industry field
      const industry = datasetData.industry || '';
      
      // Extract year range from temporalCoverage or yearRange field
      let yearRange = '';
      if (datasetData.temporalCoverage) {
        if (typeof datasetData.temporalCoverage === 'string') {
          yearRange = datasetData.temporalCoverage;
        } else if (datasetData.temporalCoverage.start && datasetData.temporalCoverage.end) {
          const startYear = new Date(datasetData.temporalCoverage.start).getFullYear();
          const endYear = new Date(datasetData.temporalCoverage.end).getFullYear();
          yearRange = `${startYear}-${endYear}`;
        }
      } else if (datasetData.yearRange) {
        yearRange = datasetData.yearRange;
      }
      
      // Populate datasetDetails - ensure at least some data is set even if fields are missing
      this.datasetDetails = {
        uploadedBy: datasetData.provider || datasetData.organisation || datasetData.organization || '',
        organisationType: organisationType || '',
        industry: industry || '',
        organizationName: organizationName || '',
        yearRange: yearRange || '',
        datasetPermission: this.helperService.extractAccessBadge(datasetData) || 'OPEN',
        lastUpdated: this.formatService.formatDate(datasetData.itemCreatedAt || datasetData.createdAt || datasetData.lastUpdated) || '',
        fileFormat: datasetData.fileFormat || datasetData.format || '',
        license: datasetData.license || datasetData.licence || '',
        uploadFrequency: datasetData.uploadFrequency || datasetData.updateFrequency || 'One-time',
        dataReadiness: datasetData.dataReadiness || datasetData.quality || datasetData.dataQuality || 0,
        verifiedBy: datasetData.verifiedBy || datasetData.provider || datasetData.organisation || datasetData.organization || '',
        resourceType: datasetData.resourceType || datasetData.resource_type || '',
        accessPolicy: datasetData.accessPolicy || datasetData.access_policy || '',
        publishStatus: datasetData.publishStatus || datasetData.publish_status || ''
      };
      
      // Ensure datasetDetails has at least one property to pass hasDatasetDetails check
      if (Object.keys(this.datasetDetails).length === 0) {
        // Fallback: set at least one property to ensure content displays
        this.datasetDetails.uploadedBy = datasetData.label || datasetData.name || 'Unknown';
      }
      
      // Dataset card details: updatedAt in DD-MMM-YYYY HH:mm format (ensure short month, 24h time)
      const badge = this.helperService.extractAccessBadge(datasetData);
      const lastUpdatedRaw = datasetData.lastUpdated;
      const updatedAtFormatted = lastUpdatedRaw ? this.formatUpdatedAtForCard(lastUpdatedRaw) : '';
      this.datasetCardDetails = {
        badge: badge || 'Public',
        createdAt: this.formatService.formatDateInIST(datasetData.itemCreatedAt || datasetData.createdAt) || '',
        updatedAt: updatedAtFormatted || '',
        fileFormat: datasetData.fileFormat || datasetData.format || '',
        uploadFrequency: datasetData.uploadFrequency || datasetData.updateFrequency || 'One-time',
        verifiedBy: datasetData.provider || datasetData.organisation || datasetData.organization || '',
        dataReadiness: datasetData.dataReadiness || ''
      };
      
      // Additional Information
      this.additionalInformation = datasetData.additionalInfo || datasetData.notes || datasetData.description || '';
      
      // Store full dataset data for accessing resourceServer
      this.fullDatasetData = datasetData;

      // Populate Activity Overview from Item API metrics (use API values as-is; do not add 1)
      const metrics = datasetData.metrics || datasetData;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.views = metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;

      // If current tab is Details but dataset has no FILE (View file), switch to dataset tab
      // Details tab is always visible; no redirect away from it based on FILE resource.
      
      // Log publishStatus for debugging
      console.log('Dataset publishStatus:', {
        publishStatus: datasetData.publishStatus || datasetData.publish_status,
        fullDatasetDataPublishStatus: this.fullDatasetData?.publishStatus,
        datasetDetailsPublishStatus: this.datasetDetails?.['publishStatus']
      });
      
      // Load sample data only when item API returns dataSample / data_sample
      const rawSample = datasetData.dataSample ?? datasetData.data_sample;
      if (rawSample) {
        if (typeof rawSample === 'string') {
          try {
            this.sampleData = JSON.parse(rawSample);
          } catch (error) {
            console.warn('Failed to parse dataSample as JSON, using as-is:', error);
            this.sampleData = rawSample;
          }
        } else if (typeof rawSample === 'object' && rawSample !== null) {
          this.sampleData = rawSample;
        }
        console.log('Sample data loaded from API:', this.sampleData);
      } else {
        this.sampleData = null;
      }

      // Build data descriptors from item API dataDescriptor (intuitive list for modal)
      const metaKeys = ['@context', 'type', 'dataDescriptorLabel', 'description'];
      this.dataDescriptors = [];
      const rawDescriptor = datasetData.dataDescriptor ?? datasetData.data_descriptor;
      if (rawDescriptor && typeof rawDescriptor === 'object' && !Array.isArray(rawDescriptor)) {
        for (const [key, value] of Object.entries(rawDescriptor)) {
          if (metaKeys.includes(key)) continue;
          if (value == null || typeof value !== 'object') continue;
          const v = value as Record<string, unknown>;
          const dataSchema = (v['dataSchema'] ?? v['data_schema'] ?? '').toString();
          const label = (v['label'] ?? key).toString();
          const description = (v['description'] ?? '').toString();
          const rawType = v['type'];
          const typeVal = Array.isArray(rawType)
            ? rawType
            : rawType != null && rawType !== ''
              ? [String(rawType)]
              : undefined;
          if (dataSchema || label || description || (typeVal && typeVal.length > 0)) {
            this.dataDescriptors.push({
              name: key,
              label: label || key,
              dataSchema: dataSchema || '—',
              description: description || '—',
              type: typeVal?.length ? typeVal : undefined
            });
          }
        }
        console.log('Data descriptors loaded from API:', this.dataDescriptors.length);
      }

      console.log('Dataset details populated:', this.datasetDetails);
      console.log('hasDatasetDetails:', this.hasDatasetDetails);
      
      // Check bookmark and like status if user is logged in
      if (this.isLoggedIn) {
        await this.checkBookmarkAndLikeStatus();
      }
      
      // Trigger change detection to update the view
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error loading dataset details:', error);
      // Update header to show error state
      this.headerConfig = {
        ...this.headerConfig,
        description: 'Failed to load dataset information. Please try again later.'
      };
      // Keep default/fallback values on error
      this.cdr.detectChanges();
    }
    
    // Call hasAccess API to check user access after dataset details are loaded
    // Don't await this - it shouldn't block content display
    this.checkUserAccess().catch(error => {
      console.error('Error checking user access:', error);
      // Don't block content display if access check fails
    });
    
    // Load similar datasets based on tags
    // Don't await this - it shouldn't block content display
    this.loadSimilarDatasets().catch(error => {
      console.error('Error loading similar datasets:', error);
      // Don't block content display if similar datasets fail to load
    });

    // If user is on Details, Visualisation or SQL Playground tab (e.g. landed with ?tab=details), load that tab's data now that datasetId is set
    if (this.activeTab === 'details' || this.activeTab === 'visualisation' || this.activeTab === 'sql-playground') {
      this.loadDetailsTabData();
    }
  }

  async checkUserAccess(): Promise<void> {
    console.log('checkUserAccess called, datasetId:', this.datasetId);
    if (!this.datasetId) {
      console.warn('Cannot check access: datasetId is null or undefined');
      return;
    }

    // Don't call has_access API when accessPolicy is OPEN
    const accessPolicy = (this.datasetDetails?.accessPolicy ?? this.datasetDetails?.['access_policy'] ?? '').toString().toUpperCase();
    if (accessPolicy === 'OPEN') {
      this.accessStatus = { type: 'urn:dx:apdServerPanel:success' };
      console.log('Access policy is OPEN, skipping has_access API');
      return;
    }

    // Check if user is logged in
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
      console.log('User logged in status:', this.isLoggedIn);
      
      if (!this.isLoggedIn) {
        // Don't call API if user is not logged in
        console.log('User is not logged in, skipping access check');
        return;
      }

      const payload = {
        itemId: this.datasetId
      };
      console.log('Calling hasAccess API with payload:', payload);
      try {
        const response = await this.aclApdApiService.hasAccess(payload);
        console.log('Has access response:', response);
        
        // Store the access status from response
        if (response) {
          this.accessStatus = response;
        }
      } catch (error: any) {
        // Handle HTTP error responses - the API returns error data in the error body
        console.log('Has access API error (may contain status info):', error);
        
        // Extract error data from HTTP error response
        if (error?.error) {
          // The error body contains the access status information
          this.accessStatus = error.error;
          console.log('Extracted access status from error response:', this.accessStatus);
          // If access not OK yet, switch off Details/Visualisation/SQL Playground so RS/file APIs are not used
          if (this.shouldDisableRsActionsUntilAccessOk && (this.activeTab === 'details' || this.activeTab === 'visualisation' || this.activeTab === 'sql-playground')) {
            this.activeTab = 'dataset';
            this.router.navigate([], { relativeTo: this.route, queryParams: { tab: 'dataset' }, queryParamsHandling: 'merge', replaceUrl: true });
          }
        } else {
          // Reset access status if no error data available
          this.accessStatus = {};
        }
      }
    } catch (error) {
      console.error('Error checking user access:', error);
      // Don't block the page load if access check fails
      // Reset access status on error
      this.accessStatus = {};
    }
  }
  
  async onGetData(): Promise<void> {
    if (!this.datasetId) {
      console.error('Cannot get data: datasetId is null or undefined');
      return;
    }
    if (this.shouldDisableRsActionsUntilAccessOk) return;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.googleAnalytics.trackDatasetGetDataClick(this.headerConfig.title ?? '', this.datasetId ?? '');
    const list = this.resourceServersList as ResourceServer[];
    const nameMatches = (s: ResourceServer, t: string) => String(s?.name ?? '').toUpperCase().includes(t);
    const hasQ = (s: ResourceServer, t: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((q) => String(q).toUpperCase().includes(t));
    const hasOgcStac = list.some((s) => nameMatches(s, 'OGC') && hasQ(s, 'STAC'));
    const hasOgcFeature = list.some((s) => nameMatches(s, 'OGC') && hasQ(s, 'FEATURE'));
    const isFileOnly = this.hasFileResource() && !this.hasQueryType('ATTR');
    if (hasOgcStac || hasOgcFeature) {
      this.openServerSpecificGetDataModal();
    } else if (isFileOnly) {
      this.showGetDataModal = true;
      this.getDataMode = 'file';
      this.getDataPdfUrl = null;
      this.getDataFileDownloadUrl = null;
      this.getDataFileName = '';
      await this.loadGetDataFile();
    } else {
      this.openServerSpecificGetDataModal();
    }
  }

  /** Decide which Get Data modal to show based on resourceServer configuration (IUDX-style). */
  private openServerSpecificGetDataModal(): void {
    const list = this.resourceServersList as ResourceServer[];

    const nameMatches = (server: ResourceServer, target: string): boolean => {
      const name = (server?.name ?? '').toString().toUpperCase();
      return name === target || name.includes(target);
    };

    const hasAccessType = (server: ResourceServer, type: string): boolean => {
      if (!Array.isArray(server.accessTypes)) return false;
      return server.accessTypes.some((t) => String(t).toUpperCase() === type);
    };

    const hasQueryType = (server: ResourceServer, type: string): boolean => {
      if (!Array.isArray(server.queryTypes)) return false;
      const target = type.toUpperCase();
      return server.queryTypes.some((t) => String(t).toUpperCase().includes(target));
    };

    // 1. OGC with queryTypes STAC → OGC STAC Data popup
    const ogcStacServer = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'STAC'),
    );
    if (ogcStacServer) {
      this.featureDataMode = {
        type: 'OGC_STAC',
        accessTypes: ogcStacServer.accessTypes ?? [],
      };
      this.isFeatureDataOpen = true;
      this.cdr.markForCheck();
      return;
    }

    // 2. OGC with queryTypes FEATURE → OGC Feature Data popup
    const ogcFeatureServer = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'FEATURE'),
    );
    if (ogcFeatureServer) {
      this.featureDataMode = {
        type: 'OGC_FEATURE',
        accessTypes: ogcFeatureServer.accessTypes ?? [],
      };
      this.isFeatureDataOpen = true;
      this.cdr.markForCheck();
      return;
    }

    // 3. GATEWAY
    const gatewayServer = list.find((s) => nameMatches(s, 'GATEWAY'));
    if (gatewayServer) {
      const accessTypes = Array.isArray(gatewayServer.accessTypes)
        ? gatewayServer.accessTypes
        : [];
      const hasAttr = accessTypes.some((t) => String(t).toUpperCase() === 'ATTR');
      const hasTemporal = accessTypes.some((t) => String(t).toUpperCase() === 'TEMPORAL');
      const dataModel: 'model1' | 'model2' =
        hasAttr && hasTemporal ? 'model2' : 'model1';

      this.featureDataMode = {
        type: 'GATEWAY',
        dataModel,
        accessTypes,
      };
      this.isFeatureDataOpen = true;
      this.cdr.markForCheck();
      return;
    }

    // 4. Fallback – use default GATEWAY-style
    this.featureDataMode = {
      type: 'DEFAULT',
      dataModel: 'model1',
      accessTypes: [],
    };
    this.isFeatureDataOpen = true;
    this.cdr.markForCheck();
  }

  onCloseFeatureData(): void {
    this.isFeatureDataOpen = false;
    this.cdr.markForCheck();
  }

  get featureDataResourceItem(): { id: string; label: string; ogcServerBaseUrl?: string } {
    const meta = this.datasetDetails;
    const id = this.datasetId ?? 'dataset';
    const list = this.resourceServersList as ResourceServer[];
    const nameMatches = (s: any, n: string) => String(s?.name ?? '').toUpperCase().includes(n);
    const hasAccessType = (s: any, type: string) =>
      Array.isArray(s?.accessTypes) && (s.accessTypes as string[]).some((t) => String(t).toUpperCase() === type);
    const hasQueryType = (s: any, type: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((t) => String(t).toUpperCase().includes(String(type).toUpperCase()));
    const ogcFeature = list.find(
      (s) => nameMatches(s, 'OGC') && hasQueryType(s, 'FEATURE')
    );
    const ogcServerBaseUrl = (ogcFeature as any)?.url
      ? String((ogcFeature as any).url).replace(/\/$/, '')
      : undefined;
    return {
      id,
      label: meta?.organizationName
        ? `${meta.organizationName} Dataset`
        : (meta?.uploadedBy ? `${meta.uploadedBy} Dataset` : 'Dataset'),
      ogcServerBaseUrl,
    };
  }

  get stacResourceItem(): { id: string; label: string; fileFormat: string; stacServerBaseUrl?: string } {
    const meta = this.datasetDetails;
    const id = this.datasetId ?? 'dataset';
    const list = this.resourceServersList as ResourceServer[];
    const nameMatches = (s: any, n: string) => String(s?.name ?? '').toUpperCase().includes(n);
    const hasQueryType = (s: any, type: string) =>
      Array.isArray(s?.queryTypes) &&
      (s.queryTypes as string[]).some((t) => String(t).toUpperCase().includes(String(type).toUpperCase()));
    const ogcStac = list.find((s) => nameMatches(s, 'OGC') && hasQueryType(s, 'STAC'));
    const stacServerBaseUrl = (ogcStac as any)?.url
      ? String((ogcStac as any).url).replace(/\/$/, '')
      : undefined;
    return {
      id,
      label: meta?.organizationName
        ? `${meta.organizationName} Dataset`
        : (meta?.uploadedBy ? `${meta.uploadedBy} Dataset` : 'Dataset'),
      fileFormat: meta?.fileFormat ?? '',
      stacServerBaseUrl,
    };
  }

  /**
   * For datasetType=FILE: fetch file list from file server, get first file (prefer PDF) presigned URL, set getDataPdfUrl or getDataFileDownloadUrl.
   */
  async loadGetDataFile(): Promise<void> {
    if (!this.datasetId) return;
    this.getDataLoading = true;
    this.getDataPdfUrl = null;
    this.getDataFileDownloadUrl = null;
    this.getDataFileName = '';
    try {
      let files: Array<{ name: string; size?: string }> = this.versionFiles || [];
      if (files.length === 0) {
        const filesResponse = await this.fileApiService.getFilesForPreview(this.datasetId);
        const apiFiles = this.extractFilesFromFilesResponse(filesResponse?.data);
        files = apiFiles
          .filter((f: any) => f.isFile !== false)
          .map((f: any) => ({ name: f.key || f.name || '' }));
      }
      const firstPdf = files.find(f => (f.name || '').toLowerCase().endsWith('.pdf'));
      const first = firstPdf || files[0];
      if (!first?.name) {
        this.toasterService.error('No files available to display.');
        this.getDataLoading = false;
        this.cdr.markForCheck();
        return;
      }
      const { presignedUrl } = await this.fileApiService.getPresignedDownloadUrl(
        this.datasetId,
        { key: first.name, presigned: true }
      );
      const isPdf = (first.name || '').toLowerCase().endsWith('.pdf');
      if (isPdf) {
        this.getDataPdfUrl = presignedUrl;
        this.getDataFileDownloadUrl = null;
        this.getDataFileName = '';
      } else {
        this.getDataPdfUrl = null;
        this.getDataFileDownloadUrl = presignedUrl;
        this.getDataFileName = first.name;
      }
    } catch (err) {
      console.error('Error loading file for Get Data:', err);
      this.toasterService.error('Failed to load file from file server.');
    } finally {
      this.getDataLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Called when Get Data modal visibility changes; reset FILE mode URLs. */
  onGetDataModalVisibleChange(visible: boolean): void {
    if (!visible) {
      if (this.getDataPdfUrl?.startsWith('blob:')) URL.revokeObjectURL(this.getDataPdfUrl);
      if (this.getDataFileDownloadUrl?.startsWith('blob:')) URL.revokeObjectURL(this.getDataFileDownloadUrl);
      this.getDataPdfUrl = null;
      this.getDataFileDownloadUrl = null;
      this.getDataFileName = '';
    }
  }

  async loadGetData(page: number): Promise<void> {
    if (!this.datasetId) {
      return;
    }

    this.getDataLoading = true;
    try {
      const response = await this.rsApiService.entitiesNgsiLg(this.datasetId, page);
      console.log('Get Data response:', response);
      
      this.getDataResponse = response;
      
      // Extract pagination info
      if (response?.paginationInfo) {
        this.getDataTotalPages = response.paginationInfo.totalPages || 1;
        this.getDataTotalEntries = response.paginationInfo.totalCount || 0;
        this.getDataPageSize = response.paginationInfo.size || 8;
      }
      
      // Extract entries/result data
      let allEntries: any[] = [];
      if (response?.result && Array.isArray(response.result)) {
        allEntries = response.result;
      } else if (Array.isArray(response)) {
        allEntries = response;
      } else if (response?.data && Array.isArray(response.data)) {
        allEntries = response.data;
      }
      
      // Limit to 8 records per page
      this.getDataEntries = allEntries.slice(0, 8);
      
      this.getDataCurrentPage = page;
    } catch (error) {
      console.error('Error loading get data:', error);
      this.toasterService.error('Failed to load data. Please try again.');
      this.getDataEntries = [];
    } finally {
      this.getDataLoading = false;
    }
  }

  onGetDataPageChange(page: number): void {
    if (page >= 1 && page <= this.getDataTotalPages && page !== this.getDataCurrentPage) {
      this.loadGetData(page);
    }
  }

  async downloadGetDataCSV(): Promise<void> {
    if (!this.datasetId) {
      this.toasterService.warning('Dataset ID is not available');
      return;
    }

    try {
      // Call the downloadCSVNgslid API endpoint
      const blob = await this.rsApiService.downloadCSVNgslid(this.datasetId);
      
      // Create download link and trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `dataset-${this.datasetId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.cdr.detectChanges();
      this.googleAnalytics.trackDatasetDownload(this.headerConfig.title ?? '', this.datasetId ?? '');
      this.toasterService.success('CSV file downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      this.toasterService.error('Failed to download CSV file');
    }
  }

  private extractHeaders(entries: any[]): string[] {
    if (!entries || entries.length === 0) return [];
    
    const headerSet = new Set<string>();
    entries.forEach(entry => {
      this.flattenObject(entry, '', headerSet);
    });
    
    return Array.from(headerSet).sort();
  }

  private flattenObject(obj: any, prefix: string, headerSet: Set<string>): void {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          this.flattenObject(obj[key], newKey, headerSet);
        } else {
          headerSet.add(newKey);
        }
      }
    }
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  getFormattedGetData(): string {
    if (!this.getDataEntries || this.getDataEntries.length === 0) {
      return '[]';
    }
    return JSON.stringify(this.getDataEntries, null, 2);
  }

  getGetDataStartEntry(): number {
    return (this.getDataCurrentPage - 1) * this.getDataPageSize + 1;
  }

  getGetDataEndEntry(): number {
    const end = this.getDataCurrentPage * this.getDataPageSize;
    return Math.min(end, this.getDataTotalEntries);
  }

  getGetDataPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(5, this.getDataTotalPages);
    let startPage = Math.max(1, this.getDataCurrentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.getDataTotalPages, startPage + maxPages - 1);
    
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  /** Open View File modal: ensure files are loaded, then open modal and load first file. */
  async onViewFile(): Promise<void> {
    if (!this.datasetId) return;
    if (this.shouldDisableRsActionsUntilAccessOk) return;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
    } catch {
      this.showLoginModal = true;
      return;
    }

    this.googleAnalytics.trackDatasetViewFileClick(this.headerConfig.title ?? '', this.datasetId ?? '');
    if (this.versionFiles.length === 0) {
      await this.loadDetailsTabData();
    }
    if (this.versionFiles.length === 0) {
      this.toasterService.error('No files available to view.');
      return;
    }
    this.openViewFileModal();
  }

  openViewFileModal(): void {
    this.showViewFileModal = true;
    this.viewFilePdfUrl = null;
    this.viewFileDocxHtml = '';
    if (this.hasViewFilePdf && this.hasViewFileDocx) {
      this.viewFileActiveTab = 'pdf';
    } else if (this.hasViewFilePdf) {
      this.viewFileActiveTab = 'pdf';
    } else {
      this.viewFileActiveTab = 'docx';
    }
    this.loadViewFileContent();
  }

  closeViewFileModal(): void {
    this.showViewFileModal = false;
    if (this.viewFilePdfUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFilePdfUrl);
    }
    this.viewFilePdfUrl = null;
    if (this.viewFileDocxDownloadUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFileDocxDownloadUrl);
    }
    this.viewFileDocxDownloadUrl = null;
    this.viewFileDocxHtml = '';
    this.viewFileDocxFileName = '';
    this.viewFileLoading = false;
  }

  onViewFileModalVisibleChange(visible: boolean): void {
    this.showViewFileModal = visible;
    if (!visible) {
      this.closeViewFileModal();
    }
  }

  onViewFileTabChange(tab: 'pdf' | 'docx'): void {
    this.viewFileActiveTab = tab;
    this.loadViewFileContent();
  }

  async loadViewFileContent(): Promise<void> {
    if (!this.datasetId) return;
    const isPdf = this.viewFileActiveTab === 'pdf';
    const files = isPdf ? this.versionFilesPdf : this.versionFilesDocx;
    const first = files[0];
    if (!first?.name) {
      this.viewFilePdfUrl = null;
      this.viewFileDocxHtml = '';
      this.viewFileLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.viewFileLoading = true;
    if (this.viewFilePdfUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFilePdfUrl);
    }
    this.viewFilePdfUrl = null;
    if (this.viewFileDocxDownloadUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewFileDocxDownloadUrl);
    }
    this.viewFileDocxDownloadUrl = null;
    this.viewFileDocxHtml = '';
    this.cdr.markForCheck();
    try {
      const { presignedUrl } = await this.fileApiService.getPresignedDownloadUrl(
        this.datasetId,
        { key: first.name, presigned: true }
      );
      if (isPdf) {
        this.viewFilePdfUrl = presignedUrl;
        this.viewFileDocxHtml = '';
      } else {
        this.viewFilePdfUrl = null;
        this.viewFileDocxHtml = '';
        this.viewFileDocxDownloadUrl = presignedUrl;
        this.viewFileDocxFileName = first.name;
      }
    } catch (err) {
      console.error('Error loading file for view:', err);
      this.toasterService.error('Failed to load file for viewing.');
    } finally {
      this.viewFileLoading = false;
      this.cdr.markForCheck();
    }
  }

  get viewFileDocxSafeHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.viewFileDocxHtml || '');
  }

  get viewFileDocxSafeDownloadUrl(): SafeResourceUrl | null {
    return this.viewFileDocxDownloadUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.viewFileDocxDownloadUrl) : null;
  }

  get viewFilePdfSafeUrl(): SafeResourceUrl | null {
    return this.viewFilePdfUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.viewFilePdfUrl) : null;
  }

  get getDataPdfSafeUrl(): SafeResourceUrl | null {
    return this.getDataPdfUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.getDataPdfUrl) : null;
  }

  get getDataFileDownloadSafeUrl(): SafeResourceUrl | null {
    return this.getDataFileDownloadUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(this.getDataFileDownloadUrl) : null;
  }

  onCardAction(action: string): void {
    console.log('Card action clicked:', action);
    const name = this.headerConfig.title ?? '';
    const id = this.datasetId ?? '';
    if (action === 'sample-data') {
      this.googleAnalytics.trackDatasetSampleDataClick(name, id);
      this.openSampleDataModal();
    } else if (action === 'data-descriptors') {
      this.googleAnalytics.trackDatasetDataDescriptorsClick(name, id);
      this.openDataDescriptorsModal();
    } else if (action === 'view-code') {
      this.googleAnalytics.trackDatasetViewCodeClick(name, id);
      this.openViewCodeModal();
    } else if (action === 'query-data') {
      this.openQueryDataModal();
    } else {
      // TODO: Implement other action handlers
    }
  }

  openDataDescriptorsModal(): void {
    if (this.dataDescriptors.length > 0) {
      this.selectedDataElement = this.dataDescriptors[0].name;
    } else {
      this.selectedDataElement = '';
    }
    this.showDataDescriptorsModal = true;
    this.cdr.detectChanges();
  }

  closeDataDescriptorsModal(): void {
    this.showDataDescriptorsModal = false;
  }

  selectDataElement(elementName: string): void {
    this.selectedDataElement = elementName;
    this.cdr.detectChanges();
  }

  get selectedDescriptor(): any {
    return this.dataDescriptors.find(d => d.name === this.selectedDataElement) || this.dataDescriptors[0];
  }

  /** type from selected descriptor as display string (e.g. "ValueDescriptor" or "iudx:DataDescriptor"). */
  get selectedDescriptorTypeDisplay(): string {
    const t = this.selectedDescriptor?.type;
    if (t == null) return '';
    if (Array.isArray(t)) return t.length ? t.join(', ') : '';
    return String(t);
  }

  /** Sample value for the selected descriptor from item API dataSample (e.g. "Ranjangaon ganpati road" for Address). */
  get selectedDescriptorSampleValue(): string | number | boolean | null | undefined {
    const desc = this.selectedDescriptor;
    if (!desc?.name) return undefined;
    const sample = this.sampleData ?? this.fullDatasetData?.dataSample ?? this.fullDatasetData?.data_sample;
    if (!sample || typeof sample !== 'object') return undefined;
    let raw = sample[desc.name];
    if (raw === undefined && typeof sample === 'object' && !Array.isArray(sample)) {
      const key = Object.keys(sample).find(k => k.toLowerCase() === desc.name.toLowerCase());
      raw = key !== undefined ? sample[key] : undefined;
    }
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw === 'object') return JSON.stringify(raw);
    return raw;
  }

  /**
   * Format any date string (ISO or display format like "10 February, 2026 - 11:57 AM") to DD-MMM-YYYY HH:mm.
   * Ensures Updated at in Dataset details always shows short month and 24h time.
   */
  private formatUpdatedAtForCard(dateString: string): string {
    if (!dateString || typeof dateString !== 'string') return '';
    const trimmed = dateString.trim();
    if (!trimmed) return '';
    try {
      const d = new Date(trimmed);
      if (isNaN(d.getTime())) return trimmed;
      const iso = d.toISOString();
      return this.formatService.formatDate(iso) || trimmed;
    } catch {
      return trimmed;
    }
  }

  getCreatedAtDate(createdAt: string): string {
    if (!createdAt) return '';
    const parts = createdAt.split(' ');
    return parts[0] || '';
  }

  getCreatedAtTime(createdAt: string): string | null {
    if (!createdAt) return null;
    const parts = createdAt.split(' ');
    // Include time and timezone (e.g. "03:44 IST") when present
    return parts.length > 1 ? parts.slice(1).join(' ') : null;
  }

  getDatePart(dateTimeString: string): string {
    if (!dateTimeString) return '';
    const parts = dateTimeString.split(' ');
    return parts[0] || '';
  }

  getTimePart(dateTimeString: string): string | null {
    if (!dateTimeString) return null;
    const parts = dateTimeString.split(' ');
    return parts.length > 1 ? parts[1] : null;
  }

  isDateTimeFormat(value: string): boolean {
    if (!value) return false;
    // Check if it contains a space and looks like a date-time format (e.g., "16-Jul-2025 02:10")
    return value.includes(' ') && /\d{2}-\w{3}-\d{4}\s+\d{2}:\d{2}/.test(value);
  }

  openSampleDataModal(): void {
    this.showSampleDataModal = true;
  }

  closeSampleDataModal(): void {
    this.showSampleDataModal = false;
  }

  downloadSampleData(): void {
    const dataStr = this.formattedSampleData || JSON.stringify(this.sampleData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample-data.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  copySampleData(): void {
    const dataStr = this.formattedSampleData;
    if (!dataStr) return;
    navigator.clipboard.writeText(dataStr).then(() => {
      this.toasterService.success('Sample data copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.toasterService.error('Failed to copy to clipboard');
    });
  }

  get formattedSampleData(): string {
    const d = this.sampleData;
    if (d == null) return '';
    if (typeof d === 'string') {
      try {
        const parsed = JSON.parse(d);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return d;
      }
    }
    return JSON.stringify(d, null, 2);
  }

  get sampleDataLineCount(): number {
    const s = this.formattedSampleData;
    if (!s) return 0;
    return s.split('\n').length;
  }

  async openViewCodeModal(): Promise<void> {
    // Initialize tabs based on resourceServer array
    await this.initializeViewCodeTabs();
    this.showViewCodeModal = true;
  }

  async initializeViewCodeTabs(): Promise<void> {
    this.viewCodeTabs = [];
    this.viewCodeByTab = {};
    
    if (!this.fullDatasetData || !this.fullDatasetData.resourceServer) {
      // Fallback to default code if no resourceServer
      this.viewCodeTabs = [{ name: 'Default', resourceServer: null }];
      this.viewCodeByTab['Default'] = this.viewCode;
      this.viewCodeActiveTab = 'Default';
      return;
    }

    const resourceServers = Array.isArray(this.fullDatasetData.resourceServer)
      ? this.fullDatasetData.resourceServer
      : [this.fullDatasetData.resourceServer];

    // Load code files for each resourceServer
    for (const rs of resourceServers) {
      const tabName = rs.name || 'Default';
      this.viewCodeTabs.push({ name: tabName, resourceServer: rs });
      
      // Load the appropriate code file based on resourceServer name
      try {
        const codeContent = await this.loadCodeFileForResourceServer(tabName);
        this.viewCodeByTab[tabName] = codeContent;
      } catch (error) {
        console.error(`Error loading code file for ${tabName}:`, error);
        // Fallback to generated code
        this.viewCodeByTab[tabName] = this.generateCodeForResourceServer(rs);
      }
    }

    // Set first tab as active
    if (this.viewCodeTabs.length > 0) {
      this.viewCodeActiveTab = this.viewCodeTabs[0].name;
    }
  }

  async loadCodeFileForResourceServer(resourceServerName: string): Promise<string> {
    // Map resourceServer names to file names
    const fileMap: { [key: string]: string } = {
      'NGSI-LD': 'viewCode-ngsi-ld_get_paginated_data.py',
      'OGC': 'viewCode-OGC_Feature_data.py',
      'Gateway': 'viewCode-gateway_get-data.py',
      'GATEWAY': 'viewCode-gateway_get-data.py',
      'FILE': 'viewCode-FILE_data.py',
    };

    const fileName = fileMap[resourceServerName] || 'viewCode-ngsi-ld_get_paginated_data.py';
    const filePath = `/${fileName}`;

    try {
      let codeContent = await firstValueFrom(this.http.get(filePath, { responseType: 'text' }));
      
      // Replace environment variable with actual control plane URL
      const controlPlaneUrl = `${environment.basePathUrl}${environment.authApiPath}token`;
      // Replace os.getenv("CONTROL_PLANE_URL", "default") patterns
      // Pattern 1: CONTROL_PLANE_URL = os.getenv("CONTROL_PLANE_URL", "...")
      codeContent = codeContent.replace(
        /CONTROL_PLANE_URL\s*=\s*os\.getenv\s*\(\s*["']CONTROL_PLANE_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `CONTROL_PLANE_URL = "${controlPlaneUrl}"`
      );
      // Pattern 2: control_plane_url = os.getenv("CONTROL_PLANE_URL", "...")
      codeContent = codeContent.replace(
        /control_plane_url\s*=\s*os\.getenv\s*\(\s*["']CONTROL_PLANE_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `control_plane_url = "${controlPlaneUrl}"`
      );

      // OGC Features-style items URL: base from environment + collection id (dataset) + example bbox query
      const ogcBase = environment.api_ogc_server_domain.replace(/\/+$/, '');
      const ogcCollectionId = this.datasetId || 'dd60aa4f-24a3-435d-936d-2b2b7f5607a8';
      const ogcItemsQuery =
        'bbox=67.975503%2C%2023.036425%2C%2077.878989%2C%2031.824154&bbox-crs=http%3A%2F%2Fwww.opengis.net%2Fdef%2Fcrs%2FOGC%2F1.3%2FCRS84';
      const ogcApiUrl = `${ogcBase}/collections/${ogcCollectionId}/items?${ogcItemsQuery}`;
      codeContent = codeContent.replace(
        /ogc_api_url\s*=\s*os\.getenv\s*\(\s*["']OGC_API_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `ogc_api_url = "${ogcApiUrl}"`
      );

      // Replace BASE_RS_PATH_URL environment variable with actual base RS path URL
      const baseRsPathUrl = environment.baseRsPathUrl;
      codeContent = codeContent.replace(
        /BASE_RS_PATH_URL\s*=\s*os\.getenv\s*\(\s*["']BASE_RS_PATH_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `BASE_RS_PATH_URL = "${baseRsPathUrl}"`
      );
      
      // Replace BASE_FILE_PATH_URL environment variable with actual base file path URL
      const baseFilePathUrl = environment.baseFilePathUrl;
      codeContent = codeContent.replace(
        /BASE_FILE_PATH_URL\s*=\s*os\.getenv\s*\(\s*["']BASE_FILE_PATH_URL["']\s*,\s*["'][^"']+["']\s*\)/g,
        `BASE_FILE_PATH_URL = "${baseFilePathUrl}"`
      );
      
      // Replace dataset ID in the code if datasetId is available
      if (this.datasetId) {
        // Replace common patterns for dataset/entity IDs in the code
        // Pattern 1: UUIDs in URLs (e.g., /entities/76cd9fb0-4ea7-4938-b4cc-bf541f4a3efa)
        codeContent = codeContent.replace(
          /\/entities\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
          `/entities/${this.datasetId}`
        );
        // Pattern 2: UUIDs in collections (e.g., /collections/dd60aa4f-24a3-435d-936d-2b2b7f5607a8)
        codeContent = codeContent.replace(
          /\/collections\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
          `/collections/${this.datasetId}`
        );
        // Pattern 3: entity_id = "..." assignments
        codeContent = codeContent.replace(
          /entity_id\s*=\s*"[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}"/gi,
          `entity_id = "${this.datasetId}"`
        );
        // Pattern 4: Default ID in else clause
        codeContent = codeContent.replace(
          /entity_id\s*=\s*"[^"]*"\s*#\s*Default ID/gi,
          `entity_id = "${this.datasetId}"  # Default ID`
        );
        // Pattern 5: UUIDs in FILE server URLs (e.g., /v1/databanks/76cd9fb0-4ea7-4938-b4cc-bf541f4a3efa/files)
        codeContent = codeContent.replace(
          /\/v1\/databanks\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\/files/gi,
          `/v1/databanks/${this.datasetId}/files`
        );
      }
      
      return codeContent;
    } catch (error) {
      console.error(`Failed to load code file ${fileName}:`, error);
      throw error;
    }
  }

  generateCodeForResourceServer(resourceServer: any): string {
    const datasetId = this.datasetId || 'YOUR_DATASET_ID';
    const rsName = resourceServer?.name || 'NGSI-LD';
    const rsUrl = resourceServer?.url || 'rs.forestdx.iudx.io';
    
    if (rsName === 'FILE') {
      // Generate code for FILE resource server
      return `import requests
import json

# === CONFIGURATION ===
CONTROL_PLANE_URL = "https://staging.controlplane.forestdx.iudx.io/iudx/v2/auth/token"
FILE_SERVER_URL = "https://${rsUrl}/v1/databanks/${datasetId}/files"
CLIENT_ID = "Please use your client details"
CLIENT_SECRET = "Please use your client details"
TIMEOUT = 10

def get_access_token():
    """Fetch access token from Control Plane API"""
    headers = {
        "clientId": CLIENT_ID,
        "clientSecret": CLIENT_SECRET
    }
    print("🔐 Requesting access token...")
    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)
    resp.raise_for_status()
    data = resp.json()
    token = data.get("result", {}).get("access_token")
    if not token:
        raise ValueError("❌ access_token not found in response JSON")
    print("✅ Access token retrieved successfully")
    return token

def list_files(access_token):
    """List files from File Server"""
    headers = {
        "Authorization": f"Bearer {access_token}",
        "Content-Type": "application/json"
    }
    print("\\n📡 Listing files from File Server...")
    payload = {
        "maxKeys": 1000,
        "recursive": True
    }
    resp = requests.post(FILE_SERVER_URL, headers=headers, json=payload, timeout=TIMEOUT)
    resp.raise_for_status()
    data = resp.json()
    print(f"\\n✅ Files retrieved successfully")
    return data

def main():
    try:
        token = get_access_token()
        files_data = list_files(token)
        print(f"\\n📊 Files data:")
        print(json.dumps(files_data, indent=2))
        # Save to file
        with open("files_response.json", "w", encoding="utf-8") as f:
            json.dump(files_data, f, indent=2)
        print("\\n💾 Saved results to files_response.json")
    except Exception as e:
        print(f"❌ Error: {e}")

if __name__ == "__main__":
    main()`;
    } else {
      // Generate code for NGSI-LD resource server (default)
      return `import requests
import json

# === CONFIGURATION ===
CONTROL_PLANE_URL = "https://staging.controlplane.forestdx.iudx.io/iudx/v2/auth/token"
ENTITIES_URL = "https://${rsUrl}/ngsi-ld/v2/entities/${datasetId}"
CLIENT_ID = "Please use your client details"
CLIENT_SECRET = "Please use your client details"
TIMEOUT = 10

def get_access_token():
    """Fetch access token from Control Plane API"""
    headers = {
        "clientId": CLIENT_ID,
        "clientSecret": CLIENT_SECRET
    }
    print("🔐 Requesting access token...")
    resp = requests.post(CONTROL_PLANE_URL, headers=headers, timeout=TIMEOUT)
    resp.raise_for_status()
    data = resp.json()
    token = data.get("result", {}).get("access_token")
    if not token:
        raise ValueError("❌ access_token not found in response JSON")
    print("✅ Access token retrieved successfully")
    return token

def fetch_entities_paginated(access_token):
    """Fetch entities from Resource Server across all pages."""
    headers = {
        "Authorization": f"Bearer {access_token}"
    }
    print("\\n📡 Fetching entities data with pagination...")
    all_data = []
    page = 1
    # Call first page to determine totalPages
    first_url = f"{ENTITIES_URL}?page={page}"
    resp = requests.get(first_url, headers=headers, timeout=TIMEOUT)
    resp.raise_for_status()
    data = resp.json()
    all_data.append(data)
    pagination_info = data.get("paginationInfo", {})
    total_pages = pagination_info.get("totalPages", 1)
    print(f"ℹ️ Total pages available: {total_pages}")
    # Loop through remaining pages if any
    for page in range(2, total_pages + 1):
        url = f"{ENTITIES_URL}?page={page}"
        print(f"➡️ Fetching page {page}/{total_pages} ...")
        resp = requests.get(url, headers=headers, timeout=TIMEOUT)
        resp.raise_for_status()
        page_data = resp.json()
        all_data.append(page_data)
    print(f"\\n✅ Fetched {len(all_data)} pages successfully.")
    return all_data

def main():
    try:
        token = get_access_token()
        pages_data = fetch_entities_paginated(token)
        # Optional: combine results if entities are in a list
        combined_entities = []
        for page_json in pages_data:
            if isinstance(page_json, dict) and "results" in page_json:
                combined_entities.extend(page_json["results"])
            elif isinstance(page_json, list):
                combined_entities.extend(page_json)
        print(f"\\n📊 Total entities collected: {len(combined_entities)}")
        # Save to file
        with open("response.json", "w", encoding="utf-8") as f:
            json.dump(pages_data, f, indent=2)
        print("💾 Saved results to response.json")
    except Exception as e:
        print(f"❌ Error: {e}")

if __name__ == "__main__":
    main()`;
    }
  }

  setViewCodeTab(tabName: string): void {
    this.viewCodeActiveTab = tabName;
  }

  getCurrentViewCode(): string {
    return this.viewCodeByTab[this.viewCodeActiveTab] || this.viewCode;
  }

  closeViewCodeModal(): void {
    this.showViewCodeModal = false;
  }

  downloadCode(): void {
    const code = this.getCurrentViewCode();
    const codeBlob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(codeBlob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = this.viewCodeActiveTab ? `code_${this.viewCodeActiveTab.toLowerCase().replace(/\s+/g, '_')}.py` : 'code.py';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  copyCode(): void {
    const code = this.getCurrentViewCode();
    navigator.clipboard.writeText(code).then(() => {
      this.toasterService.success('Code copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.toasterService.error('Failed to copy code');
    });
  }

  async openQueryDataModal(): Promise<void> {
    if (this.shouldDisableRsActionsUntilAccessOk) return;
    // Check if user is logged in
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      
      if (!isLoggedIn) {
        // Show login modal if not logged in
        this.showLoginModal = true;
        return;
      }
      
      // User is logged in, open query data modal (date range filter)
      this.showQueryDataModal = true;
      this.queryDataDateRange = { startDate: null, endDate: null };
    } catch (error) {
      console.error('Error checking login status:', error);
      // If there's an error checking login, show login modal as fallback
      this.showLoginModal = true;
    }
  }

  async loadUserOrganisation(): Promise<void> {
    try {
      // Call getUserInfo API
      const userData = await this.authApiService.getUserInfo();
      this.setCurrentUserSubFromUserData(userData);
      this.hasLoadedCurrentUserInfo = true;
      console.log('User data response:', userData);

      if (userData && userData['result']) {
        const result = userData['result'];
        console.log('User result:', result);
        console.log('Organisation object:', result['organisation']);

        // Track organisation status so we can avoid pre-populating when pending
        let organisationStatus: string | null = null;

        // First, check if organisationName or name is directly available in user data
        let organisationName: string | null = null;
        if (result['organisation']) {
          const org = result['organisation'];
          // Read status and normalise
          const rawStatus = org['status'] ?? null;
          organisationStatus = rawStatus ? String(rawStatus).toLowerCase().trim() : null;

          // If status is pending, do NOT pre-populate organisation name
          if (organisationStatus !== 'pending') {
            // Check for organisationName first, then name as fallback
            organisationName = org['organisationName'] ||
                              org['name'] ||
                              null;
            console.log('Checking organisation fields - organisationName:', org['organisationName'], 'name:', org['name'], 'status:', organisationStatus);
            if (organisationName) {
              console.log('Organisation name found directly in user data:', organisationName);
            } else {
              console.log('No organisation name found in organisation object');
            }
          } else {
            console.log('Organisation status is pending; will not pre-populate organisation name.');
          }
        } else {
          console.log('No organisation object found in user result');
        }

        // If organisationName is not directly available and status is not pending,
        // try to get it via organisationId
        if (!organisationName && organisationStatus !== 'pending') {
          let organisationId: string | null = null;
          if (result['organisation'] && result['organisation']['organisationId']) {
            organisationId = result['organisation']['organisationId'];
          }
          console.log('Organisation ID from user:', organisationId);
          
          if (organisationId) {
            // Call getAllOrganisations API
            const organisationsResponse = await this.authApiService.getAllOrganisations(1, 100);
            console.log('Organisations response:', organisationsResponse);
            
            // Extract organisations list from response
            let organisations: any[] = [];
            // Handle direct array response (as shown in network tab)
            if (Array.isArray(organisationsResponse)) {
              organisations = organisationsResponse;
            } else if (organisationsResponse && organisationsResponse['result']) {
              organisations = Array.isArray(organisationsResponse['result']) 
                ? organisationsResponse['result'] 
                : organisationsResponse['result']['organisations'] || [];
            }
            console.log('Organisations list:', organisations);
            console.log('Organisations count:', organisations.length);
            
            // Match organisationId with list and get the name
            const matchedOrganisation = organisations.find((org: any) => {
              const orgId = org.id || org.organisationId || org.organisation_id;
              console.log('Comparing orgId:', orgId, 'with organisationId:', organisationId);
              return orgId === organisationId;
            });
            
            console.log('Matched organisation:', matchedOrganisation);
            
            if (matchedOrganisation) {
              // Prioritize orgName as it's the field name in the API response
              organisationName = matchedOrganisation.orgName || matchedOrganisation.name || matchedOrganisation.organisationName;
              console.log('Organisation name found from organisations list:', organisationName);
            } else {
              console.warn('No matching organisation found for ID:', organisationId);
            }
          } else {
            console.warn('No organisation ID found in user data');
          }
        }
        
        // If we have organisationName AND status is not pending, populate the field and
        // disable it; user is treated as having an approved organisation.
        if (organisationName && organisationStatus !== 'pending') {
          this.queryDataForm.organisationName = organisationName;
          this.queryDataForm.requestAs = 'Organisation';
          this.isOrganisationNameDisabled = true;
          this.userHasOrganisation = true;
          console.log('Set organisation name in form and disabled field:', this.queryDataForm.organisationName);
        } else {
          this.isOrganisationNameDisabled = false;
          this.userHasOrganisation = false;
          console.log('No organisation name found, show Individual/Organisation choice');
        }
      }
    } catch (error) {
      console.error('Error loading user organisation:', error);
      this.isOrganisationNameDisabled = false;
      this.userHasOrganisation = false;
    }
  }

  private async loadCurrentUserInfo(force = false): Promise<void> {
    if (!this.isLoggedIn) {
      this.currentUserSub = null;
      this.hasLoadedCurrentUserInfo = false;
      return;
    }

    if (this.hasLoadedCurrentUserInfo && !force) {
      return;
    }

    try {
      const userData = await this.authApiService.getUserInfo();
      this.setCurrentUserSubFromUserData(userData);
    } catch (error) {
      console.error('Error loading current user info:', error);
      this.currentUserSub = this.getCurrentUserSubFromKeycloak();
    } finally {
      this.hasLoadedCurrentUserInfo = true;
    }
  }

  private setCurrentUserSubFromUserData(userData: any): void {
    const result = userData?.['result'] ?? userData;
    const sub = result?.['sub'] ?? this.getCurrentUserSubFromKeycloak();
    this.currentUserSub = typeof sub === 'string' && sub.trim() ? sub.trim() : null;
  }

  private getCurrentUserSubFromKeycloak(): string | null {
    try {
      const instance = this.keycloak.getKeycloakInstance() as any;
      const parsedSub = instance?.tokenParsed?.sub;
      const subject = instance?.subject;
      const sub = parsedSub ?? subject ?? null;
      return typeof sub === 'string' && sub.trim() ? sub.trim() : null;
    } catch {
      return null;
    }
  }

  async openTryOutModal(): Promise<void> {
    this.tryOutYamlError = '';
    this.showTryOutModal = true;
    this.tryOutYamlLoading = true;
    this.cdr.detectChanges();

    try {
      await this.ensureTryOutSpecLoaded();
    } catch (error) {
      console.error('Error loading Try Out documentation:', error);
      this.tryOutYamlError = 'Unable to load the API documentation right now.';
    } finally {
      this.tryOutYamlLoading = false;
      this.cdr.markForCheck();
    }
  }

  onTryOutModalVisibleChange(visible: boolean): void {
    this.showTryOutModal = visible;
    if (!visible) {
      this.tryOutYamlLoading = false;
      this.tryOutYamlError = '';
    }
  }

  private async ensureTryOutSpecLoaded(): Promise<void> {
    const targetYamlUrl = this.getTryOutYamlUrlForDataset();

    if (
      this.tryOutSpec &&
      this.tryOutOperations.length > 0 &&
      this.loadedTryOutYamlUrl === targetYamlUrl
    ) {
      return;
    }

    const yamlText = await firstValueFrom(
      this.http.get(targetYamlUrl, { responseType: 'text' })
    );

    const parsed = parseYaml(yamlText) as TryOutSpec;
    const envServerUrl = ((environment as { apiExplorerServerUrl?: string }).apiExplorerServerUrl || '').trim();
    if (envServerUrl) {
      const existingServers = parsed?.servers ?? [];
      const normalizedEnvServerUrl = envServerUrl.replace(/\/+$/, '');
      const envServerAlreadyPresent = existingServers.some(
        (server) => (server?.url || '').trim().replace(/\/+$/, '') === normalizedEnvServerUrl
      );
      if (!envServerAlreadyPresent) {
        parsed.servers = [{ url: envServerUrl }, ...existingServers];
      }
    }

    this.tryOutSpec = parsed;
    this.loadedTryOutYamlUrl = targetYamlUrl;
    this.selectedTryOutServerUrl = envServerUrl || parsed?.servers?.[0]?.url || '';
    this.tryOutOperations = this.buildTryOutOperations(parsed);
    this.expandedTryOutOperationId = this.tryOutOperations[0]?.id ?? null;
    await this.initializeTryOutState();
  }

  private getTryOutYamlUrlForDataset(): string {
    const candidates: string[] = [];

    const rs = this.fullDatasetData?.resourceServer;
    if (Array.isArray(rs)) {
      rs.forEach((server: any) => {
        candidates.push(
          String(server?.type ?? ''),
          String(server?.name ?? ''),
          String(server?.resourceType ?? ''),
          String(server?.datasetType ?? '')
        );
      });
    } else if (rs && typeof rs === 'object') {
      candidates.push(
        String(rs?.type ?? ''),
        String(rs?.name ?? ''),
        String(rs?.resourceType ?? ''),
        String(rs?.datasetType ?? '')
      );
    }

    const apis = this.fullDatasetData?.iudxResourceAPIs;
    if (Array.isArray(apis)) {
      apis.forEach((api: unknown) => candidates.push(String(api ?? '')));
    }

    candidates.push(
      String(this.datasetDetails?.resourceType ?? ''),
      String(this.fullDatasetData?.resourceType ?? ''),
      String(this.fullDatasetData?.resource_type ?? '')
    );

    const normalized = candidates.map((v) => v.trim().toLowerCase()).filter(Boolean);
    const hasNgsiLd = normalized.some((v) => v === 'ngsi-ld' || v === 'ngsi_ld' || v === 'ngsild');
    if (hasNgsiLd) return this.ngsiLdTryOutUrl;

    const hasFile = normalized.some((v) => v === 'file');
    if (hasFile) return this.fileTryOutUrl;

    return this.fileTryOutUrl;
  }

  private buildTryOutOperations(spec: TryOutSpec): TryOutOperation[] {
    const operations: TryOutOperation[] = [];
    const validMethods = new Set(['get', 'post', 'put', 'delete', 'patch', 'head', 'options']);
    const paths = spec.paths ?? {};

    Object.entries(paths).forEach(([path, pathConfig]) => {
      const sharedParameters = Array.isArray((pathConfig as any)?.parameters)
        ? (pathConfig as any).parameters
        : [];

      Object.entries(pathConfig ?? {}).forEach(([method, operation]) => {
        if (!validMethods.has(method.toLowerCase())) {
          return;
        }

        const operationParameters = Array.isArray((operation as any)?.parameters)
          ? (operation as any).parameters
          : [];
        const mergedParameters = this.mergeTryOutParameters(sharedParameters, operationParameters);
        const requestContentEntries = Object.entries((operation as any)?.requestBody?.content ?? {});
        const [requestContentType, requestContentValue] = requestContentEntries[0] ?? [null, null];

        operations.push({
          id: `${method}:${path}`,
          method: method.toUpperCase(),
          path,
          summary: (operation as any)?.summary ?? '',
          description: (operation as any)?.description ?? '',
          parameters: mergedParameters.map((param: any) => ({
            name: param.name ?? '',
            in: (param.in ?? 'query') as 'path' | 'query' | 'header',
            required: !!param.required,
            description: param.description ?? '',
            type: param.schema?.type ?? 'string',
            example: String(param.example ?? param.schema?.example ?? ''),
          })),
          requestContentType,
          requestBodyExample: this.formatTryOutExample((requestContentValue as any)?.example),
        });
      });
    });

    // Hide token-generation endpoints from Try Out.
    // Authentication should happen via Login modal/Keycloak flow instead.
    return operations.filter((operation) => !this.isTokenEndpointOperation(operation));
  }

  private isTokenEndpointOperation(operation: TryOutOperation): boolean {
    const normalizedPath = operation.path.toLowerCase();
    const normalizedSummary = (operation.summary || '').toLowerCase();
    const normalizedDescription = (operation.description || '').toLowerCase();

    const pathLooksLikeToken =
      normalizedPath === '/token' ||
      normalizedPath.endsWith('/token') ||
      normalizedPath.includes('/token/');
    const textLooksLikeToken =
      normalizedSummary.includes('token') || normalizedDescription.includes('token');

    return pathLooksLikeToken || textLooksLikeToken;
  }

  private mergeTryOutParameters(sharedParameters: any[], operationParameters: any[]): any[] {
    const merged = new Map<string, any>();
    [...sharedParameters, ...operationParameters].forEach((parameter) => {
      const key = `${parameter?.name ?? ''}:${parameter?.in ?? ''}`;
      merged.set(key, parameter);
    });
    return Array.from(merged.values());
  }

  private formatTryOutExample(example: unknown): string {
    if (example == null || example === '') {
      return '';
    }
    if (typeof example === 'string') {
      return example;
    }
    try {
      return JSON.stringify(example, null, 2);
    } catch {
      return String(example);
    }
  }

  private getDefaultTryOutParamValue(parameter: TryOutParameter, token?: string): string {
    const normalizedName = parameter.name.toLowerCase();

    if (parameter.in === 'header' && normalizedName === 'authorization' && token) {
      return `Bearer ${token}`;
    }

    if (parameter.in === 'path' && (normalizedName === 'databankid' || normalizedName === 'datasetid') && this.datasetId) {
      return this.datasetId;
    }

    return parameter.example ?? '';
  }

  private async initializeTryOutState(): Promise<void> {
    const token = await this.keycloak.getToken().catch(() => undefined);

    this.tryOutRequestState = {};
    this.tryOutExecutionResults = {};

    this.tryOutOperations.forEach((operation) => {
      const params = operation.parameters.reduce<Record<string, string>>((acc, parameter) => {
        acc[parameter.name] = this.getDefaultTryOutParamValue(parameter, token);
        return acc;
      }, {});

      this.tryOutRequestState[operation.id] = {
        params,
        bodyText: operation.requestBodyExample,
      };

      this.tryOutExecutionResults[operation.id] = {
        loading: false,
      };
    });
  }

  toggleTryOutOperation(operationId: string): void {
    this.expandedTryOutOperationId = operationId;
  }

  isTryOutOperationExpanded(operationId: string): boolean {
    return this.expandedTryOutOperationId === operationId;
  }

  setTryOutParamValue(operationId: string, paramName: string, value: string): void {
    const state = this.tryOutRequestState[operationId];
    if (!state) return;
    state.params[paramName] = value;
  }

  setTryOutRequestBody(operationId: string, value: string): void {
    const state = this.tryOutRequestState[operationId];
    if (!state) return;
    state.bodyText = value;
  }

  resetTryOutOperation(operation: TryOutOperation): void {
    const defaults = this.tryOutOperations.find((item) => item.id === operation.id);
    const state = this.tryOutRequestState[operation.id];
    if (!defaults || !state) return;

    const authorizationValue = defaults.parameters.find(
      (parameter) => parameter.in === 'header' && parameter.name.toLowerCase() === 'authorization'
    )?.name;
    const currentAuthorizationToken = authorizationValue ? state.params[authorizationValue] : undefined;
    const token = currentAuthorizationToken?.startsWith('Bearer ') ? currentAuthorizationToken.slice(7) : undefined;

    defaults.parameters.forEach((parameter) => {
      state.params[parameter.name] = this.getDefaultTryOutParamValue(parameter, token);
    });
    state.bodyText = defaults.requestBodyExample;
    this.tryOutExecutionResults[operation.id] = { loading: false };
  }

  getTryOutMethodClass(method: string): string {
    return `try-out-custom__method try-out-custom__method--${method.toLowerCase()}`;
  }

  get activeTryOutOperation(): TryOutOperation | null {
    if (!this.expandedTryOutOperationId) {
      return this.tryOutOperations[0] ?? null;
    }
    return this.tryOutOperations.find((operation) => operation.id === this.expandedTryOutOperationId) ?? null;
  }

  get activeTryOutExecutionResult(): TryOutExecutionResult | null {
    const operation = this.activeTryOutOperation;
    if (!operation) {
      return null;
    }
    return this.tryOutExecutionResults[operation.id] ?? null;
  }

  async executeCustomTryOut(operation: TryOutOperation): Promise<void> {
    this.expandedTryOutOperationId = operation.id;
    const state = this.tryOutRequestState[operation.id];
    if (!state) return;

    const isLoggedIn = await this.keycloak.isLoggedIn().catch(() => false);
    if (!isLoggedIn) {
      // Ensure login modal is visible above the Try Out modal overlay.
      this.showTryOutModal = false;
      setTimeout(() => {
        this.showLoginModal = true;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }, 0);
      this.tryOutExecutionResults[operation.id] = {
        loading: false,
        error: 'Please login to execute this API.',
      };
      this.cdr.markForCheck();
      return;
    }

    const result: TryOutExecutionResult = { loading: true };
    this.tryOutExecutionResults[operation.id] = result;

    try {
      const token = await this.keycloak.getToken().catch(() => undefined);
      const headers: Record<string, string> = {};
      let resolvedPath = operation.path;
      const queryParams = new URLSearchParams();

      operation.parameters.forEach((parameter) => {
        const value = state.params[parameter.name] ?? '';
        if (!value) return;

        if (parameter.in === 'path') {
          resolvedPath = resolvedPath.replace(`{${parameter.name}}`, encodeURIComponent(value));
        } else if (parameter.in === 'query') {
          queryParams.set(parameter.name, value);
        } else if (parameter.in === 'header') {
          headers[parameter.name] = value;
        }
      });

      if (!headers['Authorization'] && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let body: BodyInit | undefined;
      if (operation.requestContentType) {
        if (operation.requestContentType === 'application/json' && state.bodyText.trim()) {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(JSON.parse(state.bodyText));
        } else if (operation.requestContentType === 'application/x-www-form-urlencoded' && state.bodyText.trim()) {
          headers['Content-Type'] = 'application/x-www-form-urlencoded';
          const parsedBody = JSON.parse(state.bodyText);
          const formBody = new URLSearchParams();
          Object.entries(parsedBody ?? {}).forEach(([key, value]) => {
            formBody.set(key, value == null ? '' : String(value));
          });
          body = formBody.toString();
        }
      }

      const baseUrl = this.selectedTryOutServerUrl || this.tryOutSpec?.servers?.[0]?.url || '';
      const queryString = queryParams.toString();
      const requestUrl = `${baseUrl}${resolvedPath}${queryString ? `?${queryString}` : ''}`;

      result.curl = this.buildTryOutCurl(operation.method, requestUrl, headers, body);

      const response = await fetch(requestUrl, {
        method: operation.method,
        headers,
        body,
      });

      const responseText = await response.text();
      let formattedBody = responseText;
      try {
        formattedBody = JSON.stringify(JSON.parse(responseText), null, 2);
      } catch {}

      result.loading = false;
      result.status = response.status;
      result.statusText = response.statusText;
      result.body = formattedBody;
      result.error = response.ok ? undefined : `Request failed with status ${response.status}`;
    } catch (error: any) {
      result.loading = false;
      result.error = error?.message || 'Request execution failed.';
    } finally {
      this.tryOutExecutionResults[operation.id] = { ...result };
      this.cdr.markForCheck();
    }
  }

  private buildTryOutCurl(method: string, url: string, headers: Record<string, string>, body?: BodyInit): string {
    const headerFlags = Object.entries(headers)
      .map(([key, value]) => `-H "${key}: ${value}"`)
      .join(' ');

    let bodyFlag = '';
    if (typeof body === 'string' && body.length > 0) {
      bodyFlag = ` --data '${body.replace(/'/g, "\\'")}'`;
    }

    return `curl -X ${method} "${url}"${headerFlags ? ` ${headerFlags}` : ''}${bodyFlag}`;
  }

  closeQueryDataModal(): void {
    this.showQueryDataModal = false;
    this.queryDataResponse = null;
  }

  /** Called when Query Data modal visibility changes (e.g. user clicks X). Aborts the API call if one is in progress. */
  onQueryDataModalVisibleChange(visible: boolean): void {
    this.showQueryDataModal = visible;
    if (!visible) {
      // If modal is being closed, abort any in-flight request and reset state
      if (this.queryDataLoading) {
        this.queryDataAbortController?.abort();
        this.queryDataLoading = false;
      }
      this.queryDataResponse = null;
    }
  }

  /** Format Date to YYYY-MM-DD for API. */
  private formatDateForApi(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Query Data modal: apply date range filter (Search) – calls NGSI-LD v2 entities search API. */
  async onQueryDataSearch(): Promise<void> {
    if (!this.isQueryDataFormValid) {
      if (this.queryDataDateError) {
        this.toasterService.error(this.queryDataDateError);
      } else {
        this.toasterService.error('Please select a date range.');
      }
      return;
    }
    const from = this.queryDataDateRange?.startDate ? this.formatDateForApi(this.queryDataDateRange.startDate) : '';
    const to = this.queryDataDateRange?.endDate ? this.formatDateForApi(this.queryDataDateRange.endDate) : '';
    if (!this.datasetId) {
      this.toasterService.error('Dataset ID is not available.');
      return;
    }
    // Format dates as IST (e.g. 2026-01-19T00:00:00+0530, 2026-01-20T23:59:59+0530)
    const timeFrom = `${from}T00:00:00+0530`;
    const timeTo = `${to}T23:59:59+0530`;
    const payload = {
      searchCriteria: [
        {
          searchType: 'betweenTemporal',
          field: 'observationDateTime',
          values: [timeFrom, timeTo],
        },
      ],
    };
    this.queryDataLoading = true;
    this.queryDataAbortController = new AbortController();
    try {
      const response = await this.catalogueApiService.searchEntities(
        this.datasetId,
        payload,
        environment.baseRsPathUrl,
        { page: 1, size: 10 }
      );
      this.queryDataResponse = response;
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        return;
      }
      console.error('Entities search API error:', err);
      this.toasterService.error(
        err?.error?.detail ?? err?.message ?? 'Failed to load temporal data. Please try again.'
      );
    } finally {
      this.queryDataLoading = false;
      this.queryDataAbortController = null;
    }
  }

  /** Query Data modal: clear date range (Reset). */
  onQueryDataReset(): void {
    this.queryDataDateRange = { startDate: null, endDate: null };
    this.queryDataResponse = null;
  }

  /** Query Data modal: update date range and clear response when user changes selection. */
  onQueryDataDateRangeChange(value: DateRange | null): void {
    this.queryDataDateRange = value ?? { startDate: null, endDate: null };
    this.queryDataResponse = null;
  }

  /** True when both start and end dates are set and end >= start. */
  get isQueryDataFormValid(): boolean {
    const from = this.queryDataDateRange?.startDate;
    const to = this.queryDataDateRange?.endDate;
    if (!from || !to) return false;
    return to >= from;
  }

  /** Validation error message for date range, or null if valid. */
  get queryDataDateError(): string | null {
    const from = this.queryDataDateRange?.startDate;
    const to = this.queryDataDateRange?.endDate;
    if (!from || !to) return null;
    if (to < from) return 'End date must be on or after start date.';
    return null;
  }

  /** Pretty-printed JSON string for display in Query Data modal. */
  getQueryDataResponseFormatted(): string {
    if (!this.queryDataResponse) return '';
    return JSON.stringify(this.queryDataResponse, null, 2);
  }

  /** Download query data response as JSON file. */
  downloadQueryDataJson(): void {
    if (!this.queryDataResponse) return;
    const jsonStr = JSON.stringify(this.queryDataResponse, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const from = this.queryDataDateRange?.startDate ? this.formatDateForApi(this.queryDataDateRange.startDate) : 'start';
    const to = this.queryDataDateRange?.endDate ? this.formatDateForApi(this.queryDataDateRange.endDate) : 'end';
    link.download = `query-data_${from}_to_${to}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  get purposeDescriptionLength(): number {
    return this.queryDataForm.purposeDescription.length;
  }

  get isPurposeDescriptionValid(): boolean {
    return this.purposeDescriptionLength <= this.PURPOSE_MAX_LENGTH;
  }

  get isFormValid(): boolean {
    const organisationNameValid = this.queryDataForm.requestAs === 'Individual' ||
      (this.queryDataForm.requestAs === 'Organisation' && this.queryDataForm.organisationName.trim().length > 0);
    return organisationNameValid &&
           this.queryDataForm.purposeDescription.trim().length > 0 &&
           this.isPurposeDescriptionValid &&
           this.queryDataForm.agreeToTerms;
  }

  async submitQueryDataRequest(): Promise<void> {
    if (!this.isFormValid || !this.datasetId) {
      return;
    }

    try {
      const payload = {
        itemId: this.datasetId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: this.queryDataForm.requestAs,
          purposeOfRequest: this.queryDataForm.purposeDescription,
          organisationName: this.queryDataForm.requestAs === 'Organisation' ? this.queryDataForm.organisationName : ''
        }
      };

      const response = await this.aclApdApiService.createAccessRequest(payload);
      console.log('Access request created successfully:', response);
      
      // Show success toaster if API returns 200
      if (response) {
        this.toasterService.success("You're request is sent, Successfully");
      }
      
      // Close modal on success
      this.closeQueryDataModal();
      
      // Call hasAccess API to update the button status (Request Access -> Request Sent)
      await this.checkUserAccess();
    } catch (error) {
      console.error('Error creating access request:', error);
      // Error handling - you might want to show a toast notification here
    }
  }

  openTagsModal(): void {
    this.showTagsModal = true;
  }

  closeTagsModal(): void {
    this.showTagsModal = false;
  }

  get accessType(): string {
    // Get access type from badge (Public, Restricted, etc.)
    const badge = this.datasetCardDetails.badge || this.datasetDetails.datasetPermission || '';
    return badge.toLowerCase();
  }

  get isOpenAccess(): boolean {
    // Check if access type is open/public or if accessPolicy is OPEN
    const access = this.accessType;
    const accessPolicy = (this.datasetDetails.accessPolicy || '').toUpperCase();
    return access === 'open' || access === 'public' || accessPolicy === 'OPEN';
  }

  get isRestrictedAccess(): boolean {
    // Check if access type is restricted
    // Can be determined from badge/permission or accessPolicy field
    const badgeOrPermission = this.accessType === 'restricted';
    const accessPolicy = (this.datasetDetails.accessPolicy || '').toLowerCase();
    const isRestrictedFromPolicy = accessPolicy === 'restricted' || accessPolicy === 'secure' || accessPolicy === 'private';
    
    return badgeOrPermission || isRestrictedFromPolicy;
  }

  get canShowQueryData(): boolean {
    if (!this.fullDatasetData || !this.fullDatasetData.resourceServer) return false;
    return this.hasQueryType('TEMPORAL');
  }

  async onRequestAccess(): Promise<void> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
      this.showRequestAccessModal = true;
      await this.loadUserOrganisation();
    } catch {
      this.showLoginModal = true;
    }
  }

  closeRequestAccessModal(): void {
    this.showRequestAccessModal = false;
  }

  goToJoinOrganisation(): void {
    this.closeRequestAccessModal();
    this.router.navigate(['/consumer/join-organisation']);
  }

  async submitRequestAccess(): Promise<void> {
    if (!this.isFormValid || !this.datasetId) return;
    try {
      const payload = {
        itemId: this.datasetId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: this.queryDataForm.requestAs,
          purposeOfRequest: this.queryDataForm.purposeDescription,
          organisationName: this.queryDataForm.requestAs === 'Organisation' ? this.queryDataForm.organisationName : ''
        }
      };
      const response = await this.aclApdApiService.createAccessRequest(payload);
      if (response) {
        this.googleAnalytics.trackRequestAccess(this.headerConfig.title ?? '', this.datasetId ?? '');
        this.toasterService.success("You're request is sent, Successfully");
      }
      this.closeRequestAccessModal();
      await this.checkUserAccess();
    } catch (error) {
      console.error('Error creating access request:', error);
      this.toasterService.error('Failed to send access request. Please try again.');
    }
  }

  get requestAccessButtonText(): string {
    const accessType = this.accessStatus.type || '';
    if (accessType === 'urn:dx:apdServerPanel:access-pending') {
      return 'Request Sent';
    }
    return 'Request Access';
  }

  get isRequestAccessButtonDisabled(): boolean {
    const accessType = this.accessStatus.type || '';
    return accessType === 'urn:dx:apdServerPanel:access-pending';
  }

  get shouldShowRequestAccessButton(): boolean {
    // Show button if dataset has restricted access AND accessPolicy is not OPEN
    // BUT hide if user already has access (has_access returned 200 OK)
    const accessPolicy = (this.datasetDetails.accessPolicy || '').toUpperCase();
    const hasAccess = this.accessStatus.type === 'urn:dx:apdServerPanel:success';
    
    // Don't show Request Access button if user already has access
    if (hasAccess || this.isDatasetOwner) {
      return false;
    }
    
    return this.isRestrictedAccess && accessPolicy !== 'OPEN';
  }

  /** True when accessPolicy is Restricted and has_access returned 403 with no-access. Disable Get Data, Query Data, View File. */
  get hasNoAccessToRsOrFile(): boolean {
    if (!this.isRestrictedAccess) return false;
    if (this.isDatasetOwner) return false;
    const accessType = this.accessStatus.type || '';
    return accessType === 'urn:dx:apdServerPanel:no-access';
  }

  /** True until has_access returns 200 OK (restricted datasets). Disable Get Data, View File, Query Data until then. */
  get shouldDisableRsActionsUntilAccessOk(): boolean {
    if (!this.isRestrictedAccess) return false;
    if (this.isDatasetOwner) return false;
    const accessType = this.accessStatus.type || '';
    return accessType !== 'urn:dx:apdServerPanel:success';
  }

  /** True when user has no access (no-access or no policy). Disable Details, Visualisation, SQL Playground tabs and show "No Access yet" tooltip. */
  get hasNoAccessForDetailsTabs(): boolean {
    if (!this.isRestrictedAccess) return false;
    if (this.isDatasetOwner) return false;
    const accessType = (this.accessStatus.type || '').toString().trim();
    if (accessType === 'urn:dx:apdServerPanel:no-access') return true;
    if (accessType === 'no policy' || accessType.toLowerCase() === 'no-policy') return true;
    return false;
  }

  get isDatasetOwner(): boolean {
    const ownerUserId = (
      this.fullDatasetData?.ownerUserId ??
      this.fullDatasetData?.owner_user_id ??
      this.fullDatasetData?.ownerUserID ??
      this.datasetDetails?.['ownerUserId'] ??
      ''
    ).toString().trim();
    const currentSub = this.currentUserSub ?? this.getCurrentUserSubFromKeycloak();
    return !!ownerUserId && !!currentSub && ownerUserId === currentSub;
  }

  get isPublishStatusPending(): boolean {
    // Treat all common under-review states as pending.
    const rawPublishStatus = String(
      this.fullDatasetData?.publishStatus ??
      this.fullDatasetData?.publish_status ??
      this.fullDatasetData?.status ??
      this.fullDatasetData?.reviewStatus ??
      this.fullDatasetData?.approvalStatus ??
      this.datasetDetails?.['publishStatus'] ??
      this.datasetDetails?.['publish_status'] ??
      ''
    )
      .toLowerCase()
      .trim();

    if (!rawPublishStatus) {
      return false;
    }

    return ['pending', 'under_review', 'in_review', 'review', 'submitted', 'awaiting'].some(
      (state) => rawPublishStatus.includes(state)
    );
  }

  get isPublishStatusDeclined(): boolean {
    const publishStatus = 
      this.fullDatasetData?.publishStatus || 
      this.fullDatasetData?.publish_status || 
      this.datasetDetails?.['publishStatus'] ||
      '';
    const s = publishStatus && typeof publishStatus === 'string' ? publishStatus.toUpperCase().trim() : '';
    return s === 'DECLINED' || s === 'REJECTED' || s === 'DENIED';
  }

  /**
   * True when either:
   *   (A) the API response does not include a dataUploadStatus key, OR
   *   (B) the dataset's publishStatus indicates an under-review / pending state.
   * When true, an informational banner is shown and all action buttons are hidden.
   */
  get isDataUploadStatusAbsent(): boolean {
    if (!this.fullDatasetData) return false;
    const status =
      this.fullDatasetData['dataUploadStatus'] ??
      this.fullDatasetData['uploadStatus'] ??
      this.fullDatasetData['data_upload_status'];
    const hasNoDataUploadStatus = status == null;
    const rawPublishStatus = String(
      this.fullDatasetData['publishStatus'] ??
      this.fullDatasetData['status'] ??
      this.fullDatasetData['reviewStatus'] ??
      this.fullDatasetData['approvalStatus'] ??
      ''
    ).toLowerCase();
    const isReviewState = ['under_review', 'pending', 'submitted', 'in_review', 'review', 'awaiting'].some(
      (s) => rawPublishStatus.includes(s)
    );
    return hasNoDataUploadStatus || isReviewState;
  }

  /**
   * Check if dataset has mediaURL (Github repo link)
   */
  get hasMediaURL(): boolean {
    return !!(this.fullDatasetData?.mediaURL);
  }

  /**
   * Get the mediaURL value
   */
  get mediaURL(): string | null {
    return this.fullDatasetData?.mediaURL || null;
  }

  /**
   * Handle Github Repo button click - redirect to mediaURL
   */
  onGithubRepoClick(): void {
    const url = this.mediaURL;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('No mediaURL found for dataset');
    }
  }

  async loadSimilarDatasets(): Promise<void> {
    // Only search if we have tags
    if (!this.datasetTags || this.datasetTags.length === 0) {
      console.log('No tags available for similar datasets search');
      this.similarDatasets = [];
      return;
    }

    try {
      console.log('Loading similar datasets with tags:', this.datasetTags);
      
      // Build search criteria payload
      const searchCriteria: SearchCriteria[] = [
        {
          field: 'tags',
          searchType: 'term',
          values: this.datasetTags
        },
        {
          field: 'type',
          searchType: 'term',
          values: ['adex:DataBank']
        }
      ];

      console.log('Calling search API with searchCriteria:', JSON.stringify(searchCriteria, null, 2));
      
      // Call search API with searchCriteria (type filter is already in searchCriteria)
      const response = await this.catalogueApiService.searchAllAssets(
        '', // No search query
        [], // Empty assetTypes - type filter is in searchCriteria
        [], // No custom filter
        1, // page
        10, // size - limit to 10 similar datasets
        'name:asc;itemCreatedAt:asc;', // sort
        searchCriteria // Search criteria with tags and type filter
      );

      console.log('Search API response received:', response);
      
      // Extract items from response - handle different response structures
      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response?.result && Array.isArray(response.result)) {
        items = response.result;
      } else if (response?.items && Array.isArray(response.items)) {
        items = response.items;
      } else if (response?.data && Array.isArray(response.data)) {
        items = response.data;
      } else if (response?.data?.result && Array.isArray(response.data.result)) {
        items = response.data.result;
      } else if (response?.data?.items && Array.isArray(response.data.items)) {
        items = response.data.items;
      }
      
      console.log('Extracted items from response:', items.length);
      if (items.length > 0) {
        console.log('First item sample:', JSON.stringify(items[0], null, 2));
      }

      // Filter out current dataset and map to AssetCardData format
      // Don't limit the number - carousel will show 3 at a time and allow navigation
      this.similarDatasets = items
        .filter((item: any) => {
          const itemId = item.id || item['@id'] || '';
          const isNotCurrent = itemId && itemId !== this.datasetId;
          if (!isNotCurrent) {
            console.log('Filtered out current dataset:', itemId);
          }
          return isNotCurrent;
        })
        .map((item: any) => this.mapApiItemToAssetCard(item));

      console.log('Similar datasets loaded:', this.similarDatasets.length);
      console.log('Similar datasets data:', JSON.stringify(this.similarDatasets, null, 2));
    } catch (error) {
      console.error('Error loading similar datasets:', error);
      // On error, set empty array
      this.similarDatasets = [];
    }
  }

  /**
   * Map API item to AssetCardData format
   */
  private mapApiItemToAssetCard(item: any): AssetCardData {
    const id = item.id || item['@id'] || '';
    const title = item.label || item.name || 'Untitled';
    const provider = item.provider || item.organisation || item.organization || item.provider_name || '';
    
    // Extract tags
    const tags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      tags.push(...item.tags);
    }

    // Extract metadata
    const metadata: any = {};
    
    // Extract resource server type (for metadata.type)
    if (item.resourceServer) {
      if (Array.isArray(item.resourceServer) && item.resourceServer.length > 0) {
        const resourceServer = item.resourceServer[0];
        metadata.type = resourceServer?.type || resourceServer?.name || '';
      } else if (typeof item.resourceServer === 'object') {
        metadata.type = item.resourceServer.type || item.resourceServer.name || '';
      }
    }
    
    // Extract file format
    metadata.format = item.fileFormat || item.format || '';
    
    // Extract percentage (data readiness or quality score)
    metadata.percentage = item.percentage || item.dataReadiness || item.quality || item.dataQuality || undefined;
    
    // Extract date using card format
    metadata.date = this.formatService.formatDateForCard(item.itemCreatedAt || item.createdAt || item.created_at || new Date());

    // Extract badge
    const badgeLabel = this.helperService.extractAccessBadge(item) || 'Public';
    const badgeColor = this.helperService.getBadgeColorVariant(badgeLabel);

    console.log('Mapped item:', { id, title, provider, tags: tags.length, metadata, badgeLabel });

    return {
      id: id,
      title: title,
      provider: provider,
      tags: tags,
      metadata: metadata,
      badge: {
        label: badgeLabel,
        color: badgeColor as any
      }
    };
  }

  /**
   * Open Data Readiness PDF in modal viewer
   */
  async openDataReadinessPdf(): Promise<void> {
    if (!this.datasetId) {
      console.warn('Dataset ID is not available');
      return;
    }

    try {
      const pdfUrl = `${environment.dataReadinessS3Url}${this.datasetId}/data_readiness_report.pdf`;
      console.log('Loading PDF from:', pdfUrl);
      
      // Use HttpClient to fetch the PDF as a blob
      const blob = await firstValueFrom(
        this.http.get(pdfUrl, { 
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        })
      );
      
      console.log('PDF blob received, size:', blob.size);
      
      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);
      console.log('Blob URL created:', blobUrl);
      
      this.dataReadinessPdfUrl = blobUrl;
      this.showDataReadinessPdfModal = true;
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load Data Readiness Report PDF. Please try again.');
    }
  }

  /**
   * Data readiness as a number 0–100 for the circular progress (handles comma/decimal and string).
   */
  get dataReadinessPercent(): number {
    const v = this.datasetCardDetails?.dataReadiness;
    if (v == null || v === '' || v === 'NA') return 0;
    const num = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(num) ? Math.min(100, Math.max(0, num)) : 0;
  }

  /**
   * Stroke-dasharray for the readiness circle (circumference ≈ 100 for r=15.915) so the fill matches the percentage.
   */
  get readinessProgressDashArray(): string {
    const circumference = 100;
    const dash = (this.dataReadinessPercent / 100) * circumference;
    return `${dash} ${circumference}`;
  }

  /**
   * Close Data Readiness PDF modal
   */
  closeDataReadinessPdfModal(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.dataReadinessPdfUrl) {
      URL.revokeObjectURL(this.dataReadinessPdfUrl);
    }
    this.showDataReadinessPdfModal = false;
    this.dataReadinessPdfUrl = null;
  }
}

